

// Viewer3D offers public methods for developers to use.
// Viewer3DImpl is the implementation file for Viewer3D and is only used by Viewer3D.js
//
// Viewer3D does things like parameter validation.
// Viewer3DImpl does the actual work, by interfacing with other internal components, such as the MaterialManager.

import * as THREE from "three";
import { isMobileDevice, isTouchDevice, isChrome, isIOSDevice, getGlobal } from "../compat";
import { logger } from "../logger/Logger";
import { Prefs2D, VIEW_TYPES } from "./PreferenceNames";
import { DtPrefs, DtPrefs3D } from "../dt/preferences/DtPreferenceNames";
import { TandemProfile } from "../dt/preferences/DtProfile";
import { displayUnitsEnum, displayUnitsPrecisionEnum } from '../measurement/DisplayUnits';
import { getParameterByName } from "../globals";
import { errorCodeString, ErrorCodes } from "../net/ErrorCodes";
import { EventDispatcher } from "./EventDispatcher";
import { EventUtils } from "./EventUtils";
import { ScreenModeMixin, ScreenMode } from "./ScreenModeDelegate";
import { ExtensionMixin } from "./ExtensionManager";
import { HotkeyManager } from "../tools/HotkeyManager";
import { isNodeJS } from "../compat";
import { Navigation } from "../tools/Navigation";
import { ToolController } from "../tools/ToolController";
import { ViewingUtilities } from "../tools/ViewingUtilities";
import { DefaultHandler } from "../tools/DefaultHandler";
import { GestureHandler } from "../tools/GestureHandler";
import i18n from "i18next";
import * as et from "./EventTypes";
import { ViewerState } from "./ViewerState";
import { OrbitDollyPanTool } from "../tools/OrbitDollyPanTool";
import { Autocam } from "../tools/autocam/Autocam";
import { Viewer3DImpl, InitParametersSetting } from "./Viewer3DImpl";
import { KeyCode } from "../tools/KeyCode";
import { LoadingSpinner } from './LoadingSpinner';
import { ScreenShot } from "./ScreenShot";
import { GlobalManager } from './GlobalManager';
import { GlobalManagerMixin } from './GlobalManagerMixin';
import { LightPresets, DefaultLightPreset } from "./LightPresets";
import { ViewerPreferences } from "./ViewerPreferences";
import { fitToView, get3DSelectionBounds } from "../tools/FitToViewUtil";
import { EnumType } from "./EnumType";
import { DtLoader } from "../dt/loader/DtLoader";
import { ViewerSettingsPanel } from "../gui/ViewerSettingsPanel";
import { SETTINGS_PANEL_CREATED_EVENT } from "../gui/GuiViewerToolbarConst";
import { SettingsPanel } from "../gui/SettingsPanel";
import { ProgressBar } from "../gui/ProgressBar";
import { StatusBar } from "../gui/StatusBar";
import { FovTool } from "../tools/FovTool";

const av = Autodesk.Viewing;

/**
 * LMV has two extra scene it renders along with the models. One is rendered before the models, and the
 * other is rendered after the models. These scenes are THREE.Scene objects and you can add custom meshes
 * to be rendered to either of the scenes.<br>
 *
 * The objects you add to these scenes will be drawn using the same multiple render targets that are used
 * when drawing the models. You may choose to either support multiple render targets or disable them.<br>
 *
 * To support multiple render targets you can only use Prism, MeshPhongMaterial, MeshBasicMateiral or
 * LineBasicMaterial materials, and you must add all the materials to LMV.<br>
 *
 * To disable multiple render targets you can set the ``skipIdTarget`` and ``skipDepthTarget`` properties to ``true``.
 * If you disable multiple render targets your objects will not have rollover highlighting and will not
 * contribute to ambient occlusion.
 *
 * @typedef {Object} Viewer3DExtraScene
 * @property {boolean} skipIdTarget - Set to true to prevent the id target from being rendered.
 * @property {boolean} skipDepthTarget - Set to true to prevent the ambient occlusion depth target from being rendered.
 */


/**
 * Detects if WebGL is enabled.
 *
 * @return { number } -1 for not Supported,
 *                    0 for disabled
 *                    1 for enabled
 *
 * @private
 */
function detectWebGL()
{
  // Check for the webgl rendering context
  if (av.getGlobal().WebGLRenderingContext) {
    var canvas = av.getGlobal().document.createElement("canvas"),
      names = ["webgl", "experimental-webgl", "moz-webgl", "webkit-3d"],
      context = false;

    for (var i = 0; i < 4; i++) {
      try {
        context = canvas.getContext(names[i]);
        if (context && typeof context.getParameter === "function") {
          // WebGL is enabled.
          //
          return 1;
        }
        // eslint-disable-next-line no-empty
      } catch (e) {}
    }

    // WebGL is supported, but disabled.
    //
    return 0;
  }

  // WebGL not supported.
  //
  return -1;
}


var nextViewerId = 0;

// Order in which elements will appear - last node is the top most
const DefaultContainerLayersOrder = [
'markups-svg',
'pushpin-container'];



/**
 * Base class for all viewer implementations.
 * It contains everything that is needed to connect to the Autodesk's Forge service and display 2D and 3D models.
 * It also includes basic navigation support, context menu and extension APIs.
 *
 * @constructor
 *
 * @param {HTMLElement} container - The viewer container.
 * @param {ViewerConfig} config - The initial settings object.
 * @param {boolean} [config.startOnInitialize=true] - Set this to false if you want to defer the run to a later time
 *  by calling run() explicitly.
 * @param {string} [config.theme='dark-theme'] - Set this to 'light-theme' if you want to use the light ui theme. Themes can
 *  be changed during execution by calling setTheme() and passing the theme's name.
 * @param {string} [config.localStoragePrefix] - The local storage prefix for viewer.
 * @param {Settings} [options.profileSettings] - settings object to override the default viewer settings: {@link Autodesk.Viewing.ProfileSettings.Default}.
 * @param {boolean} [options.useFileProfile] - if set to true one of the registered file profiles will be used to set the profile.
 * Otherwise, the viewer uses the default profile.
 *
 * @property {Autodesk.Viewing.Navigation} navigation - The navigation api object.
 * @property {Autodesk.Viewing.ToolController} toolController - The tool controller object.
 * @property {Autodesk.Viewing.ViewingUtilities} utilities - The viewing utilities object.
 * @property {Autodesk.Viewing.Model} model - Property that references the first loaded model.
 *
* @alias Autodesk.Viewing.Viewer3D
 */
export function Viewer3D(container, config)
{
  this.setGlobalManager(new GlobalManager());

  if (container) {
    this.clientContainer = container;
    this.container = this.getDocument().createElement("div");
    this.container.classList.add("adsk-viewing-viewer");
    this.container.style.height = "100%";
    this.container.style.width = "100%";
    this.container.style.overflow = "hidden";

    this.container.classList.add(isTouchDevice() ? "touch" : "notouch");

    this.clientContainer.appendChild(this.container);

    this.config = config || {};
    this.contextMenu = null;
    this.contextMenuCallbacks = {};
    this.started = false;

    // Set the UI theme.
    this.theme = this.config.theme || 'dark-theme';
    this.container.classList.add(this.theme);

    this.containerLayerOrder = this.config.containerLayerOrder || DefaultContainerLayersOrder;

    if (isChrome()) {
      this.container.classList.add('quality-text');
    }

    // Create the canvas if it doesn't already exist
    if (this.container.nodeName === "CANVAS") {
      throw 'Viewer must be initialized on a div [temporary]';
    } else

    {
      this.canvasWrap = this.getDocument().createElement("div");
      this.canvasWrap.classList.add("canvas-wrap");

      this.canvas = this.getDocument().createElement("canvas");
      this.canvas.tabIndex = 0;
      this.canvas.setAttribute('data-viewer-canvas', 'true');
      this.canvas.style.display = 'block'; // prevent default inline behavior from adding invisible height.

      this.canvasWrap.appendChild(this.canvas);
      this.container.appendChild(this.canvasWrap);
    }

    this.canvas.viewer = this; //store a pointer to the viewer in the canvas
  }

  var localStoragePrefix = config && config.localStoragePrefix || 'Autodesk.Tandem.Preferences.';
  if (!localStoragePrefix.endsWith('.')) {
    localStoragePrefix += '.';
  }

  var prefOptions = {
    // Preferences. Prefix is a bit odd, but a legacy result after refactoring.
    prefix: localStoragePrefix,
    localStorage: !isNodeJS()
  };

  this.skipEscNavHandling = config?.skipEscNavHandling ?? false;
  this._sessionId = config?.sessionId;

  this._setDoubleSided = null;
  this.prefs = new ViewerPreferences(this, prefOptions);

  // Gets assigned by the setProfile method.
  this.profile = null;

  // Add a reference to the hotkey manager as an instance variable
  this._hotkeyManager = new HotkeyManager();

  this.extensionCache = null; // Supports Extension.getCache() feature.
  this.running = false;
  this._pushedTool = '';
  this._defaultNavigationTool = '';
  this.id = nextViewerId++;
  this.impl = new Viewer3DImpl(this.canvas, this);
  this._loadingSpinner = new LoadingSpinner();
  this._loadingSpinner.setGlobalManager(this.globalManager);

}

EventDispatcher.prototype.apply(Viewer3D.prototype);
ScreenModeMixin.prototype.apply(Viewer3D.prototype);
ExtensionMixin.prototype.apply(Viewer3D.prototype);
GlobalManagerMixin.call(Viewer3D.prototype);

Viewer3D.prototype.constructor = Viewer3D;

Object.defineProperty(Viewer3D.prototype, 'model', {
  get: function () {return this.impl?.model;},
  set: function () {throw "Do not set viewer.model";}
});

/**
 * Need to keep track of viewers in document so we know when it is safe
 * to call clearPropertyWorkerCache()
 *
 * @static
 * @alias Autodesk.Viewing.Viewer3D#ViewerCount
 */
Viewer3D.ViewerCount = 0;

/**
 * Default values passed into {@link #setCanvasClickBehavior} specifying how the viewer canvas
 * will react to user input as well as other 3d-canvas related options.
 *
 * @static
 * @alias Autodesk.Viewing.Viewer3D#kDefaultCanvasConfig
 */
Viewer3D.kDefaultCanvasConfig = {
  "click": {
    "onObject": ["selectOnly"],
    "offObject": ["deselectAll"]
  },
  "clickAlt": {
    "onObject": ["setCOI"],
    "offObject": ["setCOI"]
  },
  "clickCtrl": {
    "onObject": ["selectToggle"]
    // don't deselect if user has control key down https://jira.autodesk.com/browse/LMV-1852
    //"offObject": ["deselectAll"]
  },
  "clickShift": {
    "onObject": ["selectToggle"]
    // don't deselect if user has shift key down https://jira.autodesk.com/browse/LMV-1852
    //"offObject": ["deselectAll"]
  },

  // Features that support disabling
  "disableSpinner": false,
  "disableMouseWheel": false,
  "disableTwoFingerSwipe": false
};

/**
 * The extra scene that gets rendered after the background and before any models are rendered. See {@link Viewer3DExtraScene}
 * @member scene
 * @memberOf Autodesk.Viewing.Viewer3D
 * @alias Autodesk.Viewing.Viewer3D#scene
 */
Object.defineProperty(Viewer3D.prototype, 'scene', {
  get: function () {return this.impl.scene;},
  set: function () {throw "Do not set viewer.scene";}
});

/**
 * The extra scene that gets rendered after all models are rendered. See {@link Viewer3DExtraScene}
 * @member sceneAfter
 * @memberOf Autodesk.Viewing.Viewer3D
 * @alias Autodesk.Viewing.Viewer3D#sceneAfter
 */
Object.defineProperty(Viewer3D.prototype, 'sceneAfter', {
  get: function () {return this.impl.sceneAfter;},
  set: function () {throw "Do not set viewer.sceneAfter";}
});

/**
 * @private
 */
Viewer3D.createHeadlessViewer = function () {
  var viewer = new Viewer3D();
  viewer.impl.initialize();
  viewer.impl.setLightPreset(0);
  return viewer;
};

/**
 * @callback Autodesk.Viewing.Viewer3D~onLoadModelSuccess
 * @param {Autodesk.Viewing.Model} model - Reference to the loaded model.
 */

/**
 * @callback Autodesk.Viewing.Viewer3D~onLoadModelFailure
 * @param {Number} errorCode - error number
 * @param {string} errorMessage - error message
 */

/**
 * Initializes the viewer and loads any extensions specified in the constructor's
 * config parameter. If the optional parameters are specified, the start() function will
 * use an optimized initialization sequence that results in faster model load.
 * The parameters are the same as the ones for Viewer3D.loadModel and you do not need to call loadModel
 * subsequently if the model is loaded via the call to start().
 *
 * @param {string} [url] - Optional URN or filepath to load on start.
 * @param {object} [options] - Optional path to shared property database.
 * @param {Autodesk.Viewing.Viewer3D~onLoadModelSuccess} [onSuccessCallback] - Method that gets called when initial loading is done
 * and streaming starts.
 * @param {Autodesk.Viewing.Viewer3D~onLoadModelFailure} [onErrorCallback] - Method that gets called when initial loading ends with an error.
 * @param {object} [initOptions] - Optional: Options forwarded to viewer.initialize()
 * @returns {number} 0 if the viewer has started, an error code (same as that returned by initialize()) otherwise.
 *
 * @alias Autodesk.Viewing.Viewer3D#start
 */
Viewer3D.prototype.start = function (url, options, onSuccessCallback, onErrorCallback, initOptions) {
  if (this.started) {
    return 0;
  }
  this.started = true;

  var viewer = this;

  // Initialize the renderer and related stuff
  var result = viewer.initialize(initOptions);
  if (result !== 0) {
    if (onErrorCallback) {
      setTimeout(function () {onErrorCallback(result);}, 1);
    }
    return result;
  }

  //load extensions and set navigation overrides, etc.
  //Delayed so that it runs a frame after the long initialize() call.
  //setTimeout(function() {viewer.setUp(viewer.config);}, 1);
  //DT Made this synchronous, because it's not _that_ expensive (~2ms),
  //and causes delayed initialization of viewer state that is relied upon in
  //code that may get executed before setUp is done.
  viewer.setUp(viewer.config);

  //If a model URL was given, kick off loading first, then initialize, otherwise just continue
  //with initialization immediately.
  if (url)
  this.loadModel(url, options, onSuccessCallback, onErrorCallback);

  return 0;
};

Viewer3D.prototype.setFeatureFlags = function (flags) {
  this._featureFlags = flags;
};

Viewer3D.prototype.getFeatureFlags = function () {
  return this._featureFlags;
};

Viewer3D.prototype.registerUniversalHotkeys = function ()
{
  var self = this;

  var onPress;
  var onRelease;
  var previousTool;

  /*
  // useful for debugging, when you want to force a redraw, hit "u"
  // search on ""Autodesk.ForceUpdate" below and uncomment that popHotkeys line, too.
  // Add force update hotkey
  onPress = function() {
      // _spectorDump: the fourth "true" gets Spector to dump, if uncommented in Viewer3DImpl.js
      console.log('Capture frame started.');
      self.impl.startSpectorCapture();
      self.impl.invalidate(true,true,true,true);
      return true;
  };
  this._hotkeyManager.pushHotkeys("Autodesk.ForceUpdate", [
      {
          keycodes: [KeyCode.u],
          onPress: onPress
      }
  ]);
  */


  // Add Fit to view hotkey
  onPress = function () {
    self.navigation.setRequestFitToView(true);
    return true;
  };
  self._hotkeyManager.pushHotkeys("Autodesk.FitToView", [
  {
    keycodes: [KeyCode.f],
    onPress: onPress
  }]
  );

  // Add home hotkey
  onPress = function () {
    self.navigation.setRequestHomeView(true);
    return true;
  };
  self._hotkeyManager.pushHotkeys("Autodesk.Home", [
  {
    keycodes: [KeyCode.h],
    onPress: onPress
  },
  {
    keycodes: [KeyCode.HOME],
    onPress: onPress
  }]
  );

  // Escape
  onRelease = function () {
    // handle internal GUI components before firing the event to the client
    if (self.objectContextMenu && self.objectContextMenu.hide()) {
      return true;
    }

    // Deselect
    if (self.impl.selector.hasSelection()) {
      self.clearSelection();
      return;
    }

    // Reset default navigation mode:
    if (!self.skipEscNavHandling && self.getActiveNavigationTool() !== self.getDefaultNavigationToolName()) {
      // Force unlock active tool:
      if (self.toolController)
      self.toolController.setIsLocked(false);

      self.setActiveNavigationTool();
      return;
    }

    // Show all if anything is hidden
    if (!self.areAllVisible()) {
      self.showAll();
      return;
    }

    // TODO: Could this all be unified somehow? If event listeners had priorities,
    //       we could intersperse listeners from the client and the viewer, which
    //       I think will eventually be required.

    self.dispatchEvent({ type: et.ESCAPE_EVENT });
    return true;
  };

  self._hotkeyManager.pushHotkeys("Autodesk.Escape", [
  {
    keycodes: [KeyCode.ESCAPE],
    onRelease: onRelease
  }]
  );

  // Pan
  onPress = function () {
    previousTool = self.getActiveNavigationTool();
    return self.setActiveNavigationTool("pan");
  };
  onRelease = function () {
    return self.setActiveNavigationTool(previousTool);
  };
  var hotkeys = [
  {
    keycodes: [KeyCode.SHIFT],
    onPress: onPress,
    onRelease: onRelease
  },
  {
    keycodes: [KeyCode.SPACE],
    onPress: onPress,
    onRelease: onRelease
  }];
  self._hotkeyManager.pushHotkeys("Autodesk.Pan", hotkeys, { tryUntilSuccess: true });
};

Viewer3D.prototype.createControls = function () {
  var self = this;
  var impl = self.impl;

  self.navigation = new Navigation(impl.camera);
  self.__initAutoCam(impl);

  self.utilities = new ViewingUtilities(impl, self.autocam, self.navigation);
  self.clickHandler = new DefaultHandler(impl, self.navigation, self.utilities);
  self.toolController = new ToolController(impl, self, self.autocam, self.utilities, self.clickHandler);
  self.toolController.registerTool(new GestureHandler(self));

  self.toolController.registerTool(self._hotkeyManager);
  self.toolController.activateTool(self._hotkeyManager.getName());

  self.registerUniversalHotkeys();

  self.toolController.registerTool(new OrbitDollyPanTool(impl, self, this.config.navToolsConfig), (enable) => {
    if (!enable) {
      this.setActiveNavigationTool();
    }
  });

  self.toolController.registerTool(new FovTool(self));

  // Set the tool Modality Map
  self.toolController.setModalityMap({
    section: { explode: false, pan: false, dolly: false },
    explode: { section: false, pan: false, dolly: false },
    measure: { 'box-selection': false },
    calibration: { 'box-selection': false },
    orbit: {},
    dolly: {},
    pan: {},
    fov: {},
    bimwalk: {},
    'box-selection': { measure: false, calibration: false }
  });

  return self.toolController;
};

/**
 * Create any DOM and canvas elements, and setup WebGL.
 *
 * @param {object} [initOptions] - optional initialization options, such as renderer, glrenderer & materialManager.
 *
 * @returns {number} 0 if initialization was successful, {@link Autodesk.Viewing.ErrorCode} otherwise.
 * @private
 */
Viewer3D.prototype.initialize = function (initOptions = {})
{

  //Set up the private viewer implementation
  this.setScreenModeDelegate(this.config ? this.config.screenModeDelegate : undefined);

  var dimensions = this.getDimensions();
  this.canvas.width = dimensions.width;
  this.canvas.height = dimensions.height;
  this.setVisibleViewportArea();

  // For Safari and WKWebView and UIWebView on ios device with retina display,
  // needs to manually rescale our canvas to get the right scaling. viewport metatag
  // alone would not work.
  if (isIOSDevice() && this.getWindow().devicePixelRatio) {
    this.canvas.width /= this.getWindow().devicePixelRatio;
    this.canvas.height /= this.getWindow().devicePixelRatio;
  }

  initOptions.webgpuEnable = this.config.webgpuEnable;

  //Call this after setting canvas size above...
  this.impl.initialize(initOptions);

  //Only run the WebGL failure logic if the renderer failed to initialize (otherwise
  //we don't have to spend time creating a GL context here, since we know it worked already
  if (!this.impl.glrenderer()) {
    var webGL = detectWebGL();
    if (webGL <= 0) {// WebGL error.
      return webGL === -1 ? ErrorCodes.BROWSER_WEBGL_NOT_SUPPORTED : ErrorCodes.BROWSER_WEBGL_DISABLED;
    }
  }

  var self = this;

  // Add a callback for the panels to resize when the viewer resizes.
  // For some reason, Safari iOS updates the DOM dimensions *after* the resize event,
  // so in that case we handle the resizing asynchronously.
  if (isIOSDevice()) {
    var _resizeTimer;
    this.onResizeCallback = function () {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(self.resize.bind(self), 500);
    };
  } else {
    this.onResizeCallback = function () {
      var oldWidth = self.impl.camera.clientWidth;
      var oldHeight = self.impl.camera.clientHeight;
      var newWidth = self.container.clientWidth;
      var newHeight = self.container.clientHeight;

      if (oldWidth !== newWidth ||
      oldHeight !== newHeight) {
        self.resize();
      }
    };
  }
  this.addWindowEventListener('resize', this.onResizeCallback, false);

  this.onScrollCallback = function () {
    self.impl.canvasBoundingclientRectDirty = true;
  };
  this.addWindowEventListener('scroll', this.onScrollCallback);

  this.initContextMenu();

  // Create a progress bar. Shows streaming.
  this.progressbar = new ProgressBar(this.container);
  this.addEventListener(et.PROGRESS_UPDATE_EVENT, this.onProgressBarUpdate);

  this.statusbar = new StatusBar(this.container);
  this.onStatusBarPrefChanged = (value) => {
    this.statusbar.toggleVisibility(value);
  };

  // Localize the viewer.
  this.localize();

  this.impl.controls = this.createControls();

  // Initialize the preference callbacks
  this.initializePrefListeners();

  this.setDefaultNavigationTool("orbit");

  if (this.impl.controls)
  this.impl.controls.setAutocam(this.autocam);

  var canvasConfig = this.config && this.config.canvasConfig ? this.config.canvasConfig : Viewer3D.kDefaultCanvasConfig;
  this.setCanvasClickBehavior(canvasConfig);


  // Allow clients not load the spinner. This is needed for embedding viewer in a WebView on mobile,
  // where the spinner makes the UI looks less 'native'.
  if (!canvasConfig.disableSpinner) {

    // Create a div containing an image: this will be a
    // spinner (aka activity indicator) that tells the user
    // that the file is loading.
    //
    // Keep reference for backwards compatibility.
    this._loadingSpinner.createDom(this.container);
  }

  // Auxiliary class to get / restore the viewer state.
  this.viewerState = new ViewerState(this);

  // The default behavior is to run the main loop immediately, unless startOnInitialize
  // is provided and is false.
  //
  if (!this.config || this.config.startOnInitialize !== false) {
    this.run();
  }

  const _window = av.getGlobal();
  if (!_window.NOP_VIEWER) {// Always keep the main viewer in NOP_VIEWER
    _window.NOP_VIEWER = this;
  }

  // Main viewer and any secondary viewers can be found in the NOP_VIEWERS array
  _window.NOP_VIEWERS = _window.NOP_VIEWERS || [];
  _window.NOP_VIEWERS.push(this);

  this.addEventListener(et.MODEL_ADDED_EVENT, function (e) {
    self.onModelAdded(e.model, e.preserveTools);
  });

  this.dispatchEvent(et.VIEWER_INITIALIZED);

  Viewer3D.ViewerCount++;

  // These calls are useful for Internet Explorer's use of spector.
  // Uncomment this code, and add the https://spectorcdn.babylonjs.com/spector.bundle.js script
  // in index.html, and Spector's menu shows up in the application itself.
  /*
  var spector = new SPECTOR.Spector();
  window.spector = spector;
  spector.displayUI();    // comment this line out if you instead want to use _spectorDump and the "u" key
  spector.spyCanvases();
  */

  return 0; // No Error initializing.
};

/**
 * Loading extensions and initializing canvas interactions.
 * Invoked automatically by {@link Autodesk.Viewing.Viewer3D#start} method.
 *
 * @param {object} [config] - configuration values
 * @param {string[]} [config.extensions] - List of extension ids to load with the model.
 * @param {object} [config.canvasConfig] - Overrides for click/tap events on the 3D canvas. Refer to {@link #setCanvasClickBehavior} for details.
 *
 * @alias Autodesk.Viewing.Viewer3D#setUp
 */
Viewer3D.prototype.setUp = function (config) {

  this.config = config || {};
  this.config.extensions = this.config.extensions || [];

  // Load the extensions specified in the config.
  //
  if (Array.isArray(this.config.extensions)) {
    var extensions = this.config.extensions;
    for (var i = 0; i < extensions.length; ++i) {
      this.loadExtension(extensions[i], this.config);
    }
  }

  //load debug ext by query param
  var debugConfig = getParameterByName("lmv_viewer_debug");
  if (debugConfig === "true") {
    this.loadExtension("Autodesk.Debug", this.config);
  }

  var canvasConfig = this.config.canvasConfig || Viewer3D.kDefaultCanvasConfig;
  this.setCanvasClickBehavior(canvasConfig);

  this._onAggregatedSelectionChanged = this._onAggregatedSelectionChanged.bind(this);
  this.addEventListener(et.AGGREGATE_SELECTION_CHANGED_EVENT, this._onAggregatedSelectionChanged);

  this.initPreferencesProfile();
  this._loadingSpinner.hide();

};

/**
 * Unloads extensions and the loaded model.
 * Invoked automatically by {@link Autodesk.Viewing.Viewer3D#finish} method.
 *
 * @alias Autodesk.Viewing.Viewer3D#tearDown
 */
Viewer3D.prototype.tearDown = function () {
  this.clearSelection();

  this.removeEventListener(et.AGGREGATE_SELECTION_CHANGED_EVENT, this._onAggregatedSelectionChanged);

  if (this.loadedExtensions) {
    for (var extensionId in this.loadedExtensions) {
      try {
        // Extensions that fail to unload will end up terminating
        // the viewer tearDown process.  Thus we protect from it
        // here and log it (if available).
        this.unloadExtension(extensionId);
      } catch (err) {
        logger.error("Failed to unload extension: " + extensionId, err, errorCodeString(ErrorCodes.VIEWER_INTERNAL_ERROR));
        logger.track(
          {
            category: "error_unload_extension",
            extensionId: extensionId,
            error_message: err.message,
            call_stack: err.stack
          });
      }
    }
    this.loadedExtensions = null;
  }

  if (this.viewerSettingsPanel) {
    this.setSettingsPanel(null);
  }

  // Deactivate mouse events and touch gestures. If a new model will be loaded, it will be enabled again (activateDefaultNavigationTools).
  if (this.toolController) {
    this.toolController.enableMouseButtons(false);
    this.toolController.deactivateTool("gestures");
  }

  if (this.statusbar) {
    this.prefs.removeListeners(DtPrefs.DISPLAY_OBJECT_INFO, this.onStatusBarPrefChanged, null);
    this.statusbar.dtor();
    this.statusbar = null;
  }

  logger.reportRuntimeStats(true);

  this._loadingSpinner.show();

  this.impl.unloadCurrentModel();
  this.impl.setCutPlaneSet('__set_view', undefined);
};

/**
 * When selection has changed set the pivot point to be in the middle, if Autodesk.Viewing.Private.DtPrefs3D.SELECTION_SETS_PIVOT is true
 *
 * @alias Autodesk.Viewing.Viewer3D#_onAggregatedSelectionChanged
 */
Viewer3D.prototype._onAggregatedSelectionChanged = function ()
{
  if (this.navigation.getSelectionSetsPivot()) {
    const selection = this.impl.selector.getAggregateSelection();
    if (selection.length) {
      const selectionBounds = get3DSelectionBounds(this, selection);
      this.utilities.setPivotPoint(selectionBounds.getCenter(new THREE.Vector3()), true, true);
      this.utilities.pivotActive(this.navigation.getPivotSetFlag(), true);
    }
  }
};

/**
 * Triggers the Viewer's render loop.
 * Invoked automatically by {@link Autodesk.Viewing.Viewer3D#start} method.
 * Refer to {@link ViewerConfig|ViewerConfig.startOnInitialize} to change start's method behavior.
 *
 * @alias Autodesk.Viewing.Viewer3D#run
 */
Viewer3D.prototype.run = function ()
{
  if (!this.running) {
    this.resize();
    this.running = true;
    this.impl.run();
  }
};


/**
 * Localize the viewer. This method can be overwritten so that the subclasses
 * can localize any additional elements. Invoked internally during initialization.
 *
 * @alias Autodesk.Viewing.Viewer3D#localize
 */
Viewer3D.prototype.localize = function ()
{
  i18n.localize();
};

Viewer3D.prototype.__initAutoCam = function (impl)
{
  var self = this;

  var ourCamera = impl.camera;

  if (!ourCamera.pivot)
  ourCamera.pivot = new THREE.Vector3(0, 0, 0);

  if (!ourCamera.target)
  ourCamera.target = new THREE.Vector3(0, 0, 0);

  if (!ourCamera.worldup)
  ourCamera.worldup = ourCamera.up.clone();

  function autocamChange(upChanged)
  {
    if (self.autocamCamera.isPerspective !== ourCamera.isPerspective)
    {
      if (self.autocamCamera.isPerspective)
      self.navigation.toPerspective();else

      self.navigation.toOrthographic();
    }
    self.navigation.setVerticalFov(self.autocamCamera.fov, false);
    self.navigation.setView(self.autocamCamera.position, self.autocamCamera.target);
    self.navigation.setPivotPoint(self.autocamCamera.pivot);
    self.navigation.setCameraUpVector(self.autocamCamera.up);
    if (upChanged)
    self.navigation.setWorldUpVector(self.autocamCamera.worldup);

    self.impl.syncCamera(upChanged);
  }

  function pivotDisplay(state)
  {
    if (self.utilities)
    self.utilities.pivotActive(state, false);else

    self.impl.controls.pivotActive(state, false);
  }

  function onTransitionCompleted() {
    self.fireEvent({ type: et.CAMERA_TRANSITION_COMPLETED });
  }

  self.autocamCamera = ourCamera.clone();
  self.autocamCamera.target = ourCamera.target.clone();
  self.autocamCamera.pivot = ourCamera.pivot.clone();
  self.autocamCamera.worldup = ourCamera.worldup.clone();

  self.autocam = new Autocam(self.autocamCamera, self.navigation, self.canvas);
  self.autocam.setGlobalManager(this.globalManager);
  self.autocam.registerCallbackCameraChanged(autocamChange);
  self.autocam.registerCallbackPivotDisplay(pivotDisplay);
  self.autocam.registerCallbackTransitionCompleted(onTransitionCompleted);

  self.addEventListener("cameraChanged", function (evt)
  {
    var ourCamera = evt.camera;
    self.autocam.sync(ourCamera);
  });

  self.autocam.sync(ourCamera);
};


/**
 * Removes all created DOM elements, performs GL uninitialization that is needed and removes event listeners.
 *
 * @alias Autodesk.Viewing.Viewer3D#uninitialize
 */
Viewer3D.prototype.uninitialize = function ()
{

  this.onResizeCallback && this.removeWindowEventListener('resize', this.onResizeCallback, false);
  this.onResizeCallback = null;

  this.onScrollCallback && this.removeWindowEventListener('scroll', this.onScrollCallback);
  this.onScrollCallback = null;

  if (this.canvas) {
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
  } else if (this.container) {
    this.container.removeEventListener('mousedown', this.onMouseDown);
  }
  this.onMouseDown = null;

  if (this.canvas) {
    this.canvas.removeEventListener('mouseup', this.onMouseUp);
  } else if (this.container) {
    this.container.removeEventListener('mouseup', this.onMouseUp);
  }
  this.onMouseUp = null;

  if (this.canvas) {
    this.canvas.parentNode.removeChild(this.canvas);
    this.canvas.viewer = null;

    // Detach canvas
    if (InitParametersSetting.canvas === this.canvas) {
      InitParametersSetting.canvas = null;
    }

    this.canvas = null;
  }


  this.removeEventListener(et.PROGRESS_UPDATE_EVENT, this.onProgressBarUpdate);
  this.progressbar = null;

  this.canvasWrap = null;

  this.viewerState = null;

  logger.reportRuntimeStats();
  logger.track({ category: "viewer_destroy" }, true);

  if (this.toolController) {
    this.toolController.uninitialize();
    this.toolController = null;
    this.clickHandler = null;
    this.utilities = null;
  }

  if (this.navigation) {
    this.navigation.uninitialize();
    this.navigation = null;
  }

  if (this.impl) {
    this.impl.dtor();
    this.impl = null;
  }

  this._loadingSpinner.destroy();
  this._loadingSpinner = null;

  this.prefs && this.prefs.clearListeners();
  this.prefs = null;
  this.profile = null;

  if (this.autocam) {
    this.autocam.dtor();
    this.autocam = null;
    this.autocamCamera = null;
  }

  //this._hotkeyManager.popHotkeys("Autodesk.ForceUpdate");
  this._hotkeyManager.popHotkeys("Autodesk.FitToView");
  this._hotkeyManager.popHotkeys("Autodesk.Home");
  this._hotkeyManager.popHotkeys("Autodesk.Escape");
  this._hotkeyManager.popHotkeys("Autodesk.Pan");
  this._hotkeyManager.popHotkeys("Autodesk.Orbit");
  this._hotkeyManager = null;

  Viewer3D.ViewerCount--;
  if (Viewer3D.ViewerCount === 0) {

    //DT clearPropertyWorkerCache();
  }
  if (this.onDefaultContextMenu) {
    this.container.removeEventListener('contextmenu', this.onDefaultContextMenu, false);
    this.onDefaultContextMenu = null;
  }

  if (this.viewerSettingsPanel) {
    this.viewerSettingsPanel.uninitialize();
    this.viewerSettingsPanel = null;
  }

  if (this.screenModeDelegate) {
    this.screenModeDelegate.uninitialize();
    this.screenModeDelegate = null;
  }

  if (this._setDoubleSided) {
    this.removeEventListener(et.MODEL_ADDED_EVENT, this._setDoubleSided);
    this._setDoubleSided = null;
  }

  this.extensionCache = null;
  this.clientContainer = null;
  this.config = null;
  this.contextMenu = null;
  this.contextMenuCallbacks = null;

  if (this.container && this.container.parentNode)
  this.container.parentNode.removeChild(this.container);
  this.container = null;

  this.dispatchEvent(et.VIEWER_UNINITIALIZED);

  //forget all event listeners
  this.listeners = {};

  const _window = av.getGlobal();
  if (_window.NOP_VIEWER === this) {
    _window.NOP_VIEWER = null;
  }

  if (_window.NOP_VIEWERS) {
    const idx = _window.NOP_VIEWERS.indexOf(this);
    if (idx !== -1) {
      _window.NOP_VIEWERS.splice(idx, 1);

      if (_window.NOP_VIEWERS.length === 0) {
        _window.NOP_VIEWERS = null;
      }
    }
  }

  logger.log("viewer destroy");
};


/**
 * Unloads any loaded extensions and then uninitializes the viewer.
 *
 * @alias Autodesk.Viewing.Viewer3D#finish
 */
Viewer3D.prototype.finish = function () {
  this.tearDown();
  this.uninitialize();
};


Viewer3D.prototype.setLoadHeuristics = function (options) {
  if (typeof options.createWireframe === "undefined") {
    options.createWireframe = !isMobileDevice() && this.prefs.get('edgeRendering');
  }
};

Viewer3D.prototype.activateDefaultNavigationTools = function (is2d) {
  if (isNodeJS())
  return;
  var defaultNavTool = is2d ? "pan" : "orbit";
  // Sets the default tool
  this.setDefaultNavigationTool(defaultNavTool);
  // Then activate the tool
  this.setActiveNavigationTool(defaultNavTool);

  if (this.toolController) {
    this.toolController.enableMouseButtons(true);
    this.toolController.activateTool("gestures");
  }
};

Viewer3D.prototype.registerDimensionSpecificHotkeys = function (is2d) {
  if (!this._hotkeyManager)
  return;

  if (is2d) {
    // Remove 3D specific hotkeys
    this._hotkeyManager.popHotkeys("Autodesk.Orbit");
  } else {

    var self = this;

    // Add 3D specific hotkeys
    // Orbit
    var previousTool;
    var onPress = function () {
      previousTool = self.getActiveNavigationTool();
      return self.setActiveNavigationTool("orbit");
    };
    var onRelease = function () {
      return self.setActiveNavigationTool(previousTool);
    };
    var hotkeys = [
    {
      keycodes: [KeyCode.ALT],
      onPress: onPress,
      onRelease: onRelease
    }];
    this._hotkeyManager.pushHotkeys("Autodesk.Orbit", hotkeys, { tryUntilSuccess: true });
  }
};

Viewer3D.prototype.onModelAdded = function (model, preserveTools) {

  if (model.is2d()) {
    this.navigation.setMinimumLineWidth(model.loader?.svf?.minLineWidth);
  }

  var models = this.impl.modelQueue().getModels();
  var isFirstModel = models.length === 1;

  if (isFirstModel) {
    this.initializeFirstModelPresets(model, preserveTools);
  }
};

Viewer3D.prototype.initializeFirstModelPresets = function (model, preserveTools, preserveView) {

  //OK, pay attention here:
  //The code here is a mess in order to make "freeorbit" as default navigation
  //unit test load the initial view correctly.
  //First, we call setActiveNavigationTool() with blank, which will make
  //"freeorbit" the active tool. This will trigger a HACK in Viewer3dImpl.setViewFromCamera
  //which checks for that and uses a different initialization code path of the initial view.
  //Only then, after this happens, we call activateDefaultNavigationTools(), which seems to
  //override the name of the active tool somehow from "freeorbit" to "orbit"
  //and would prevent the camera from initializing correctly.
  //Ideally, the "freeorbit" hack would be removed from Viewer3dImpl and moved here, but this is
  //beyond the scope of the Forge-Fluent merge at this point.

  if (!preserveView && !model.getData().loadOptions.preserveView) {
    this.setActiveNavigationTool();
    this.impl.setViewFromFile(model, true);
    this.toolController.recordHomeView();
  }

  this.registerDimensionSpecificHotkeys(model.is2d());
  if (!preserveTools)
  this.activateDefaultNavigationTools(model.is2d());

  this.navigation.setIs2D(this.impl.modelQueue().areAll2D());

  // If it is not a leaflet model, clear constrain 2d.
  // Otherwise, it will leak to the next model that viewer could open up.
  this.navigation.setConstraints2D();
};

/**
 * Loads a model into the viewer. Can be used sequentially to load multiple 3D models into the same scene.
 *
 * @param {string} url - The url to the model.
 * @param {object} [options] - Dictionary of options.
 * @param {Autodesk.Viewing.FileLoader} [options.fileLoader] - The file loader to use for this url. Required for unsupported file types.
 * @param {object} [options.loadOptions] - May contain params that are specific for certain loaders/filetypes.
 * @param {string} [options.sharedPropertyDbPath] - Optional path to shared property database.
 * @param {string} [options.ids] - A list of object IDs to load.
 * @param {boolean}  [options.loadAsHidden] - By default, a new model is instantly shown and triggers viewer refreshes
 *                                          during loading. Setting this option avoids that. The model can then be
 *                                          shown later by calling showModel().
 * @param {string} [options.modelNameOverride] - Allows host application to override model name used in UI.
 * @param {LmvMatrix4} [options.placementTransform] - Applied to the model during loading.
 * @param {string|Object} [options.applyScaling] - Unit-Scaling that is applied to the model on load, e.g. { from: 'ft', to: 'm' }.
 *                                                 If 'from' is not set, it is determined from model metadata (if provided). If only 'to' is set,
 *                                                 you can just assign a string directly, e.g. applyScaling = 'm' is the same as applyScaling = { to: 'm' }.
 * @param {boolean}[options.applyPlacementInModelUnits] - Only relevant if options.placementTransform and options.applyScaling are both used at once.
 *                                                      In this way, it controls the order in which placement and scaling happen:
 *                                                        - False: Placement happens in viewer world-units. That is, applyScaling is done first,
 *                                                          then the custom placementMatrix is applied. (Default behavior)
 *                                                        - True: Placement happens in model units. That is, custom placementMatrix is applied first,
 *                                                          then the unit scaling.
 * @param {Autodesk.Viewing.Viewer3D~onLoadModelSuccess} [onSuccessCallback] - A method that gets called when model's metadata loading is done
 * and geometry streaming starts.
 * @param {Autodesk.Viewing.Viewer3D~onLoadModelFailure} [onErrorCallback] - A method that gets called when loading fails.
 *
 * @alias Autodesk.Viewing.Viewer3D#loadModel
 */
Viewer3D.prototype.loadModel = async function (url, options, onSuccessCallback, onErrorCallback, onWorkerStart) {
  var self = this;

  // Kind of sucks, but I couldn't think of a better way because loaders
  // are so
  var reservation = self.impl._reserveLoadingFile(); // Reserve a slot for a loading file
  options = options || {};

  var loaderInstance;
  function onDone(error, model) {
    if (!self.impl) {
      return; // In case viewer was destroyed before model finished loading
    }

    self.impl._removeLoadingFile(loaderInstance);
    if (error) {
      self.dispatchEvent({ type: et.LOADER_LOAD_ERROR_EVENT, error: error, loader: loaderInstance });
      onError(error.code, error.msg, error.args);
      return;
    }

    if (options.loadAsHidden) {
      self.impl.modelQueue().addHiddenModel(model);
    } else {
      self.impl.addModel(model);
    }

    if (onSuccessCallback) {
      onSuccessCallback(model);
    }
  }

  function onError(errorCode, errorMessage, errorArgs) {
    if (onErrorCallback) {
      onErrorCallback(errorCode, errorMessage, errorArgs);
    }
  }

  var match = url.toLowerCase().match(/\.([a-z0-9]+)(\?|$)/),
    fileExtension = match ? match[1] : null;

  var loader;
  if (options && options.fileLoader) {
    loader = options.fileLoader;
  } else {
    loader = DtLoader;
  }

  // if there's no loader, don't try to create it and cause an error.
  if (!loader) {
    self.impl._removeLoadingFile(reservation);
    logger.error("File extension not supported:" + fileExtension, errorCodeString(ErrorCodes.UNSUPORTED_FILE_EXTENSION));
    onError(ErrorCodes.UNSUPORTED_FILE_EXTENSION, "File extension not supported", 0, fileExtension);
    return false;
  }

  //Run some heuristics to adapt the viewing experience to the model we are about to display
  this.setLoadHeuristics(options);

  loaderInstance = new loader(this.impl, this.config);

  self.impl._addLoadingFile(reservation, loaderInstance);
  var returnValue = loaderInstance.loadFile(url, options, onDone, onWorkerStart);
  // If the loader returns false, make sure we don't wait for it.
  if (!returnValue)
  self.impl._removeLoadingFile(loaderInstance);

  this.fireEvent({ type: et.LOADER_LOAD_FILE_EVENT, loader: loaderInstance });


  return returnValue;
};

/**
 * Unloads the specified model.
 * Reference {@link Autodesk.Viewing.Viewer3D#hideModel} to hide the model.
 * @param {Autodesk.Viewing.Model} model - The model to unload.
 *
 * @alias Autodesk.Viewing.Viewer3D#unloadModel
 */
Viewer3D.prototype.unloadModel = function (model) {
  this.impl.unloadModel(model);
};


/**
 *
 * @param {string} modelRootUrl - The Document instance, which owns the model being loaded
 * @param {ViewerConfig} [options] - Options to pass to {@link Autodesk.Viewing.Viewer3D#loadModel}. Will be initialized internally if not specified.
 *                                   The options object will be augmented by automatically determined load parameters.
 * @returns {Promise} - Resolves with an object representing the model.
 *
 * @alias Autodesk.Viewing.Viewer3D#loadDocumentNode
 */
Viewer3D.prototype.loadDocumentNode = function (modelRootUrl, options = {}) {

  if (!options.keepCurrentModels && this.impl.hasModels()) {
    let _conf = this.config;
    this.tearDown();
    this.setUp(_conf);
  }

  return new Promise((resolve, reject) => {
    // The error handler receives multiple parameters, so we construct a more meaningful error from them.
    const errorHandler = (code, message, args) => {
      const error = { message, details: args };
      reject(error);
    };
    this.loadModel(modelRootUrl, options, resolve, errorHandler);
  });
};

/**
 * Returns the dimensions of the WebGL canvas.
 *
 * @returns {object} Client rectangle bounds object { width:Number, height: Number }
 *
 * @alias Autodesk.Viewing.Viewer3D#getDimensions
 */
Viewer3D.prototype.getDimensions = function () {
  if (this.container) {
    // NB: Getting dimensions of the client container instead of the container.
    //     At least in IE11, getting dimensions on the dynamically created
    //     child of the dynamically created parent returns a 0 height.
    var rect = {};
    if (this.getScreenMode() === ScreenMode.kFullScreen) {
      rect.width = screen.width;
      rect.height = screen.height;
    } else {
      rect = this.container.getBoundingClientRect();
    }

    return {
      width: rect.width,
      height: rect.height
    };
  }

  return null;
};


/**
 * Resizes the viewer. Required when wrapping div changes dimensions due to CSS changes.
 *
 * @alias Autodesk.Viewing.Viewer3D#resize
 */
Viewer3D.prototype.resize = function ()
{
  if (this.container.clientWidth > 0 && this.container.clientHeight > 0) {
    this.impl.resize(this.container.clientWidth, this.container.clientHeight);
  }
};

/**
 * @returns {Autodesk.Viewing.HotkeyManager} The hotkey manager.
 *
 * @alias Autodesk.Viewing.Viewer3D#getHotkeyManager
 */
Viewer3D.prototype.getHotkeyManager = function ()
{
  return this._hotkeyManager;
};

/**
 * Gets the camera so it can be modified by the client.
 * @returns {THREE.Camera} The active camera.
 *
 * @alias Autodesk.Viewing.Viewer3D#getCamera
 */
Viewer3D.prototype.getCamera = function ()
{
  return this.impl.camera;
};

/**
 * Gets the view state as a plain object.
 * A viewer state contains data for the viewport, selection and isolation.
 *
 * Reference {@link Autodesk.Viewing.Viewer3D#restoreState}
 *
 * @tutorial viewer_state
 *
 * @param {object} [filter] - Specifies which viewer values to get.
 * @returns {object} Viewer state.
 *
 * @alias Autodesk.Viewing.Viewer3D#getState
 */
Viewer3D.prototype.getState = function (filter) {
  return this.viewerState.getState(filter);
};

/**
 * Restores the viewer state from a given object.
 *
 * Reference {@link Autodesk.Viewing.Viewer3D#getState}
 *
 * @tutorial viewer_state
 *
 * @param {Object} viewerState
 * @param {Object} [filter] - Similar in structure to viewerState used to filter out values
 * that should not be restored.
 * @param {boolean} [immediate] - Whether the new view is applied with (true) or without transition (false).
 * @returns {boolean} True if restore operation was successful.
 *
 * @alias Autodesk.Viewing.Viewer3D#restoreState
 * @fires Autodesk.Viewing#VIEWER_STATE_RESTORED_EVENT
 */
Viewer3D.prototype.restoreState = function (viewerState, filter, immediate) {
  var success = this.viewerState.restoreState(viewerState, filter, immediate);
  if (success) {
    this.dispatchEvent({ type: et.VIEWER_STATE_RESTORED_EVENT, value: success });
  }
  return success;
};

Viewer3D.prototype.setViewType = function (viewType) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setViewType is not applicable to 2D");
    return;
  }
  this.prefs.set(DtPrefs3D.VIEW_TYPE, viewType);
};

/**
 * Sets the click behavior on the canvas to follow config.
 * This is used to change the behavior of events such as selection or Center-of-Interest changed.
 * @example
 *  {
 *       "click": {
 *           "onObject": [ACTIONS],
 *           "offObject": [ACTIONS]
 *       },
 *       "clickCtrl": {
 *           "onObject": [ACTIONS],
 *           "offObject": [ACTIONS]
 *       },
 *       "clickShift": {
 *           ...
 *       },
 *       "clickCtrlShift": {
 *           ...
 *       },
 *       "disableSpinner": BOOLEAN
 *       "disableMouseWheel": BOOLEAN,
 *       "disableTwoFingerSwipe": BOOLEAN
 *  }
 *
 * Actions can be any of the following: "selectOnly", "selectToggle", "deselectAll", "isolate", "showAll", "setCOI", "focus", "hide"
 * @param {object} config - Parameter object that meets the above layout.
 *
 * @alias Autodesk.Viewing.Viewer3D#setCanvasClickBehavior
 */
Viewer3D.prototype.setCanvasClickBehavior = function (config)
{
  this.impl.controls.setClickBehavior?.(config);

  if (this.clickHandler)
  this.clickHandler.setClickBehavior(config);

  if (config && config.disableMouseWheel) {
    this.toolController.setMouseWheelInputEnabled(false);
  }

  if (config && config.disableTwoFingerSwipe) {
    var gestureHandler = this.toolController.getTool("gestures");
    if (gestureHandler) {
      gestureHandler.disableTwoFingerSwipe();
    }
  }
};

/**
 * Isolates one of many sub-elements. You can pass in a node or an array of nodes to isolate.
 * Pass in null to reset isolation.
 *
 * @param {number[]|number} node - A node ID or array of node IDs from the model tree {@link BaseViewer#getObjectTree}.
 * @param {Autodesk.Viewing.Model} [model] - the model that contains the node id. Defaults to the first loaded model.
 *
 * @alias Autodesk.Viewing.Viewer3D#isolate
 */
Viewer3D.prototype.isolate = function (node, model)
{
  model = model || this.model;
  if (!model) {
    return;
  }

  this.impl.visibilityManager.isolate(node, model);
};


/**
 * Sets the background colors, which will be used to create a gradient.
 * Values are in the range [0..255]
 *
 * @param {number} red
 * @param {number} green
 * @param {number} blue
 * @param {number} red2
 * @param {number} green2
 * @param {number} blue2
 *
 * @alias Autodesk.Viewing.Viewer3D#setBackgroundColor
 */
Viewer3D.prototype.setBackgroundColor = function (red, green, blue, red2, green2, blue2)
{
  this.impl.setClearColors(red, green, blue, red2, green2, blue2);
};

/**
 * Sets the background opacity.
 *
 * @param {number} opacity - Value is in the range [0.0..1.0]
 *
 * @alias Autodesk.Viewing.Viewer3D#setBackgroundOpacity
 */
Viewer3D.prototype.setBackgroundOpacity = function (opacity)
{
  this.impl.setClearAlpha(opacity);
};

/**
 * Toggles the selection for a given dbid.
 * If it was unselected, it is selected.
 * If it was selected, it is unselected.
 *
 * Currently three ways of node selection are supported:
 * - Autodesk.Viewing.SelectionType.MIXED
 *   - Leaf nodes are selected using the overlayed selection type, and inner nodes are selected using regular selection type.
 * - Autodesk.Viewing.SelectionType.REGULAR
 *   - Nodes are tinted with the selection color.
 * - Autodesk.Viewing.SelectionType.OVERLAYED
 *   - Nodes are tinted with the selection color and shown on top of the not selected geometry.
 *
 * Not yet implemented for 2D.
 *
 * @param {number} dbid
 * @param {Autodesk.Viewing.Model} [model] - the model that contains the dbId. Uses the initial model loaded by default.
 * @param {number} selectionType a member of Autodesk.Viewing.SelectionMode.
 *
 * @alias Autodesk.Viewing.Viewer3D#toggleSelect
 */
Viewer3D.prototype.toggleSelect = function (dbid, model, selectionType)
{
  model = model || this.model;
  if (model && model.is2d())
  {
    // Fails because Model.getNodeById is not supported.
    logger.warn("Viewer3D.toggleSelect is not yet implemented for 2D");
    return;
  }

  this.impl.selector.toggleSelection(dbid, model, selectionType);
};

/**
* Selects the array of ids. You can also pass in a single id instead of an array.
*
* Currently three ways of node selection are supported:
* - Autodesk.Viewing.SelectionType.MIXED
*   - Leaf nodes are selected using the overlayed selection type, and inner nodes are selected using regular selection type.
* - Autodesk.Viewing.SelectionType.REGULAR
*   - Nodes are tinted with the selection color.
* - Autodesk.Viewing.SelectionType.OVERLAYED
*   - Nodes are tinted with the selection color and shown on top of the not selected geometry.
*
* @param {number[]|number} dbids - element or array of elements to select.
* @param {Autodesk.Viewing.Model} [model] - the model instance containing the ids.
* @param {number} [selectionType] - a member of `Autodesk.Viewing.SelectionType`.
*
* @alias Autodesk.Viewing.Viewer3D#select
*/
Viewer3D.prototype.select = function (dbids, model, selectionType)
{
  if (typeof dbids === "number") {
    dbids = [dbids];
  }

  model = model || this.model;
  this.impl.selector.setSelection(dbids, model, selectionType);
};


/**
 * Clears the selection. See {@link Autodesk.Viewing.Viewer3D#select|select()}
 * @alias Autodesk.Viewing.Viewer3D#clearSelection
 */
Viewer3D.prototype.clearSelection = function ()
{
  this.impl.selector.clearSelection();
};

/**
 * Returns information about the visibility of the current selection.
 * @returns {object} `{hasVisible:boolean, hasHidden:boolean}`
 *
 * @alias Autodesk.Viewing.Viewer3D#getSelectionVisibility
 */
Viewer3D.prototype.getSelectionVisibility = function () {
  return this.impl.selector.getSelectionVisibility();
};

/**
 * Returns the number of nodes (dbIds) in the current selection.
 * @returns {number} - number of selected nodes
 * @alias Autodesk.Viewing.Viewer3D#getSelectionCount
 */
Viewer3D.prototype.getSelectionCount = function () {
  return this.impl.selector.getSelectionLength();
};

/**
 * Sets selection granularity mode. Supported values are:
 * - Autodesk.Viewing.SelectionMode.LEAF_OBJECT: Always select the leaf objects in the hierarchy.
 * - Autodesk.Viewing.SelectionMode.FIRST_OBJECT: For a given node, selects the first non-composite (layer, collection, model) on the path from the root to the given node, and all children.
 * - Autodesk.Viewing.SelectionMode.LAST_OBJECT: For a given node, selects the nearest ancestor composite node and all children.
 *   Selects the input node itself in case there is no composite node in the path to the root node.
 *
 * @param {number} mode - The selection mode.
 * @alias Autodesk.Viewing.Viewer3D#setSelectionMode
 */
Viewer3D.prototype.setSelectionMode = function (mode) {
  this.prefs.set(DtPrefs3D.SELECTION_MODE, mode);
};

/**
 * Gets selection granularity mode. See Autodesk.Viewing.Viewer3D#setSelectionMode for details
 * @returns {number}
 */
Viewer3D.prototype.getSelectionMode = function () {
  return this.prefs.get(DtPrefs3D.SELECTION_MODE);
};

/**
 * Returns the selected items from all loaded models.
 * @param {function} [callback] - Optional callback to receive enumerated pairs of model and dbId
 * for each selected object. If no callback is given, an array of objects is returned.
 * @returns {object[]} An array of objects with a model and selectionSet properties for each model
 * that has selected items in the scene.
 * @alias Autodesk.Viewing.Viewer3D#getAggregateSelection
 */
Viewer3D.prototype.getAggregateSelection = function (callback) {
  var res = this.impl.selector.getAggregateSelection();

  if (callback) {
    for (var i = 0; i < res.length; i++) {
      for (var j = 0; j < res[i].selection.length; j++) {
        callback(res[i].model, res[i].selection[j]);
      }
    }
  }

  return res;
};

/**
 *
 * @typedef {object} SelectionDef
 * @property {Autodesk.Viewing.Model} model - The model from which to select
 * @property {number[]} ids - array of ids to select
 * @property {number} [selectionType] - a member of `Autodesk.Viewing.SelectionType`
 */

/**
 * Selects ids from different models. Choose this api instead of select() when selecting across many models
 * @param {SelectionDef[]} selection - Array of selection objects defining what to select
 * @alias Autodesk.Viewing.Viewer3D#setAggregateSelection
 */
Viewer3D.prototype.setAggregateSelection = function (selection) {
  this.impl.selector.setAggregateSelection(selection);
};

/**
 * Returns the isolated items from all loaded models.
 *
 * @returns {object[]} An array of objects with a `model` and the isolated `ids` in that model.
 *
 * @alias Autodesk.Viewing.Viewer3D#getAggregateIsolation
 */
Viewer3D.prototype.getAggregateIsolation = function () {

  var res = this.impl.visibilityManager.getAggregateIsolatedNodes();
  return res;
};

/**
 * Returns the hidden nodes for all loaded models.
 *
 * @returns {object[]} An array of objects with a `model` and the hidden `ids` in that model.
 *
 * @alias Autodesk.Viewing.Viewer3D#getAggregateHiddenNodes
 */
Viewer3D.prototype.getAggregateHiddenNodes = function () {

  var res = this.impl.visibilityManager.getAggregateHiddenNodes();
  return res;
};

/**
 * Ensures the passed in nodes (dbIds) are hidden.
 *
 * @param {number[]|number} node - An array of nodes (dbids) or just a single node.
 * @param {Autodesk.Viewing.Model} [model] - The model that contains the dbId. By default uses the initial model loaded into the scene.
 *
 * @alias Autodesk.Viewing.Viewer3D#hide
 */
Viewer3D.prototype.hide = function (node, model)
{
  model = model || this.model;
  this.impl.visibilityManager.hide(node, model);
};

/**
 * Ensures the passed in nodes (dbIds) are shown.
 *
 * @param {number[]|number} node - An array of nodes (dbids) or just a single node.
 * @param {Autodesk.Viewing.Model} [model] - The model that contains the dbId. By default uses the initial model loaded into the scene.
 *
 * @alias Autodesk.Viewing.Viewer3D#show
 */
Viewer3D.prototype.show = function (node, model)
{
  model = model || this.model;
  this.impl.visibilityManager.show(node, model);
};

/**
 * Ensures everything is visible. Clears all node isolation (3D) and turns on all layers (2D).
 *
 * @alias Autodesk.Viewing.Viewer3D#showAll
 *
 * @fires Autodesk.Viewing#SHOW_ALL_EVENT
 */
Viewer3D.prototype.showAll = function ()
{
  this.impl.visibilityManager.aggregateIsolate([]);
  this.fireEvent({ type: et.SHOW_ALL_EVENT });
};

/**
 * Ensures all objects are hidden. Clears all nodes.
 *
 * @alias Autodesk.Viewing.Viewer3D#hideAll
 *
 * @fires Autodesk.Viewing#HIDE_ALL_EVENT
 */
Viewer3D.prototype.hideAll = function ()
{
  const models = this.getVisibleModels();
  models.forEach((model) => {
    this.hide(model.getRootId(), model);
  });
  this.fireEvent({ type: et.HIDE_ALL_EVENT });
};


/**
 * Returns true if every node (dbId) is visible.
 * @returns {boolean} - True if every node is visible, false otherwise.
 *
 * @alias Autodesk.Viewing.Viewer3D#areAllVisible
 */
Viewer3D.prototype.areAllVisible = function () {
  return this.impl.isWholeModelVisible(this.model);
};

/**
 * Returns true if the specified node is visible.
 * The model argument is required only when in multi-model scenarios.
 *
 * @param {number} nodeId - Geometry node to check if visible.
 * @param {Autodesk.Viewing.Model} [model] - The model that contains the specified ``nodeId``.
 *
 * @returns {boolean}
 *
 * @alias Autodesk.Viewing.Viewer3D#isNodeVisible
 */
Viewer3D.prototype.isNodeVisible = function (nodeId, model) {
  model = model || this.model;
  return this.impl.isNodeVisible(nodeId, model);
};

/**
 * Enables or disables the high quality rendering settings.
 *
 * Not applicable to 2D.
 *
 * @param {boolean} useSAO - True or false to enable screen space ambient occlusion.
 * @param {boolean} useFXAA - True or false to enable fast approximate anti-aliasing.
 *
 * @alias Autodesk.Viewing.Viewer3D#setQualityLevel
 */
Viewer3D.prototype.setQualityLevel = function (useSAO, useFXAA)
{
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setQualityLevel is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.AMBIENT_SHADOWS, useSAO);
  this.prefs.set(DtPrefs3D.ANTIALIASING, useFXAA);
};


/**
 * Toggles ghosting during search and isolate.
 *
 * Not applicable to 2D.
 *
 * @param {boolean} value - Indicates whether ghosting is on or off.
 *
 * @alias Autodesk.Viewing.Viewer3D#setGhosting
 */
Viewer3D.prototype.setGhosting = function (value)
{
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setGhosting is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.GHOSTING, value);
};

/**
 * Toggles ground shadow.
 *
 * Not applicable to 2D.
 *
 * @param {boolean} value - Indicates whether shadow is on or off.
 *
 * @alias Autodesk.Viewing.Viewer3D#setGroundShadow
 */
Viewer3D.prototype.setGroundShadow = function (value)
{
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setGroundShadow is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.GROUND_SHADOW, value);
};

/**
 * Toggles ground reflection.
 *
 * Not applicable to 2D.
 *
 * @param {boolean} value - Indicates whether reflection is on or off.
 *
 * @alias Autodesk.Viewing.Viewer3D#setGroundReflection
 */
Viewer3D.prototype.setGroundReflection = function (value)
{
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setGroundReflection is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.GROUND_REFLECTION, value);
};

/**
 * Toggles environment map for background.
 *
 * Not applicable to 2D.
 *
 * @param {boolean} value - Indicates whether environment map for background is on or off.
 *
 * @alias Autodesk.Viewing.Viewer3D#setEnvMapBackground
 */
Viewer3D.prototype.setEnvMapBackground = function (value)
{
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setEnvMapBackground is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.ENV_MAP_BACKGROUND, value);
};

/**
 * Toggles first person tool popup.
 *
 * Not applicable to 2D.
 *
 * @param {boolean} value - Indicates whether first person tool popup is showed or not.
 *
 * @alias Autodesk.Viewing.Viewer3D#setFirstPersonToolPopup
 */
Viewer3D.prototype.setFirstPersonToolPopup = function (value)
{
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setFirstPersonToolPopup is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.FIRST_PERSON_TOOL_POPUP, value);
};

/**
 * Returns the state of First Person Walk tool popup.
 *
 * Not applicable to 2D.
 *
 * @returns {boolean} true if the First Person Walk tool popup appears, false if the First Person Walk tool popup does not appear.
 *
 * @alias Autodesk.Viewing.Viewer3D#getFirstPersonToolPopup
 */
Viewer3D.prototype.getFirstPersonToolPopup = function ()
{
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.getFirstPersonToolPopup is not applicable to 2D");
    return;
  }

  return this.prefs.get('firstPersonToolPopup');
};

/**
 * Toggles the bimwalk tool popup.
 *
 * Not applicable to 2D.
 *
 * @param {boolean} value - Indicates whether first person tool popup is showed or not.
 *
 * @alias Autodesk.Viewing.Viewer3D#setBimWalkToolPopup
 *
 */
Viewer3D.prototype.setBimWalkToolPopup = function (value)
{
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setBimWalkToolPopup is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.BIM_WALK_TOOL_POPUP, value);
};

/**
 * Returns the state of First Person Walk tool popup
 *
 * Not applicable to 2D.
 *
 * @returns {boolean} true if the First Person Walk tool popup appears, false if the First Person Walk tool popup does not appear.
 *
 * @alias Autodesk.Viewing.Viewer3D#getBimWalkToolPopup
 */
Viewer3D.prototype.getBimWalkToolPopup = function ()
{
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.getBimWalkToolPopup is not applicable to 2D");
    return;
  }

  return this.prefs.get('bimWalkToolPopup');
};

/**
 * Toggles whether progressive rendering is used. Warning: turning progressive rendering off
 * will have serious performance implications.
 * @param {boolean} value whether it is on or off
 *
 * @alias Autodesk.Viewing.Viewer3D#setProgressiveRendering
 */
Viewer3D.prototype.setProgressiveRendering = function (value)
{
  this.prefs.set(DtPrefs.PROGRESSIVE_RENDERING, value);
};

/**
 * Overrides line colors in 2D models to render in shades of gray.
 * Applies only to 2D models.
 *
 * @param {boolean} value whether it is on or off
 *
 * @alias Autodesk.Viewing.Viewer3D#setGrayscale
 */
Viewer3D.prototype.setGrayscale = function (value)
{
  if (this.model && this.model.is3d()) {
    logger.warn("Viewer3D.setGrayscale is not applicable to 3D");
    return;
  }
  this.prefs.set(Prefs2D.GRAYSCALE, value);
};

/**
 * AutoCAD drawings are commonly displayed with white lines on a black background. Setting reverse swaps (just)
 * these two colors.
 * @param {boolean} value whether it is on or off
 *
 * @alias Autodesk.Viewing.Viewer3D#setSwapBlackAndWhite
 */
Viewer3D.prototype.setSwapBlackAndWhite = function (value)
{
  if (this.model && this.model.is3d()) {
    logger.warn("Viewer3D.setSwapBlackAndWhite is not applicable to 3D");
    return;
  }
  this.prefs.set(Prefs2D.SWAP_BLACK_AND_WHITE, value);
};

/**
 * Toggles whether the navigation should be optimized for performance. If set
 * to true, anti-aliasing and ambient shadows will be off while navigating.
 *
 * Not applicable to 2D.
 *
 * @param {boolean} value whether it is on or off
 *
 * @alias Autodesk.Viewing.Viewer3D#setOptimizeNavigation
 */
Viewer3D.prototype.setOptimizeNavigation = function (value)
{
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setOptimizeNaviation is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.OPTIMIZE_NAVIGATION, value);
};

/**
 * Registers rendering callbacks with preferences.
 * @private
 */
Viewer3D.prototype.initializePrefListeners = function () {
  this.prefs.addListeners(DtPrefs.KEY_MAP_CMD, (value) => {
    this.impl.toggleCmdMapping(value, this.toolController);
  });

  this.prefs.addListeners(DtPrefs3D.ANTIALIASING, (value) => {
    const useSAO = this.prefs.get(DtPrefs3D.AMBIENT_SHADOWS);
    this.impl.togglePostProcess(useSAO, value);
  });

  this.prefs.addListeners(DtPrefs3D.AMBIENT_SHADOWS, (value) => {
    const useFXAA = this.prefs.get(DtPrefs3D.ANTIALIASING);
    this.impl.togglePostProcess(value, useFXAA);
  });

  this.prefs.addListeners(DtPrefs3D.GROUND_SHADOW, (value) => {
    this.impl.toggleGroundShadow(value);
  });

  this.prefs.addListeners(DtPrefs3D.GROUND_REFLECTION, (value) => {
    this.impl.toggleGroundReflection(value);
  });

  this.prefs.addListeners(DtPrefs3D.ENV_MAP_BACKGROUND, (value) => {
    this.impl.toggleEnvMapBackground(value);
  });

  this.prefs.addListeners(DtPrefs.PROGRESSIVE_RENDERING, (value) => {
    this.impl.toggleProgressive(value);
  });

  this.prefs.addListeners(DtPrefs.ENABLE_MODEL_CACHE, (value) => {
    getGlobal().ENABLE_MODEL_CACHE = value;
  });

  this.prefs.addListeners(DtPrefs3D.VIEW_TYPE, (value) => {
    const curView = this.prefs.get(DtPrefs3D.VIEW_TYPE);

    const camera = this.impl.getModelCamera(this.model);
    if (camera) {
      const isPerspective = camera.isPerspective;
      // Update the default view from the model's stored camera.
      if (!isNaN(isPerspective)) {
        const defaultValue = isPerspective ? VIEW_TYPES.PERSPECTIVE : VIEW_TYPES.ORTHOGRAPHIC;
        this.prefs.setDefault(DtPrefs3D.VIEW_TYPE, defaultValue);
        if (curView === VIEW_TYPES.DEFAULT) this.prefs[DtPrefs3D.VIEW_TYPE] = defaultValue;
      }
    }
    this.autocam.setViewType(value);
  });

  this.prefs.addListeners(Prefs2D.GRAYSCALE, (value) => {
    this.impl.toggleGrayscale(value);
  });

  this.prefs.addListeners(Prefs2D.SWAP_BLACK_AND_WHITE, (value) => {
    this.impl.toggleSwapBlackAndWhite(value);
  });

  this.prefs.addListeners(DtPrefs3D.OPTIMIZE_NAVIGATION, (value) => {
    this.impl.setOptimizeNavigation(value);
  });

  this.prefs.addListeners(DtPrefs3D.GHOSTING, (value) => {
    this.impl.toggleGhosting(value);
  });

  this.prefs.addListeners(DtPrefs3D.LINE_RENDERING, (value) => {
    this.impl.hideLines(!value);
  });

  this.prefs.addListeners(DtPrefs.POINT_RENDERING, (value) => {
    this.impl.hidePoints(!value);
  });

  this.prefs.addListeners(DtPrefs3D.EDGE_RENDERING, (value) => {
    this.impl.setDisplayEdges(value);
  });

  this.prefs.addListeners(DtPrefs3D.LIGHT_PRESET, (value) => {
    let force = false;
    if (typeof value === 'string') {
      // find the index value
      var presetName = value;
      var idx = -1;
      for (var i = 0; i < LightPresets.length; i++) {
        if (LightPresets[i].name === presetName) {
          idx = i;
          break;
        }
      }
      if (idx === -1) {
        // Force the default light preset for environments that are not defined in LightPresets.
        logger.warn('Invalid DtPrefs3D.LIGHT_PRESET: ' + value);
        logger.warn('Setting to default light preset.');
        force = true;
      }
      value = idx;
    }
    this.impl.setLightPreset(value, force);
  });

  this.prefs.addListeners(DtPrefs3D.ALWAYS_USE_PIVOT, (value) => {
    this.navigation.setUsePivotAlways(value);
  });

  this.prefs.addListeners(DtPrefs.REVERSE_MOUSE_ZOOM_DIR, (value) => {
    this.navigation.setReverseZoomDirection(value);
  });

  this.prefs.addListeners(DtPrefs3D.REVERSE_HORIZONTAL_LOOK_DIRECTION, (value) => {
    this.navigation.setReverseHorizontalLookDirection(value);
  });

  this.prefs.addListeners(DtPrefs3D.REVERSE_VERTICAL_LOOK_DIRECTION, (value) => {
    this.navigation.setReverseVerticalLookDirection(value);
  });

  this.prefs.addListeners(DtPrefs3D.ZOOM_TOWARDS_PIVOT, (value) => {
    this.navigation.setZoomTowardsPivot(value);
  });

  this.prefs.addListeners(DtPrefs3D.SELECTION_SETS_PIVOT, (value) => {
    this.navigation.setSelectionSetsPivot(value);
  });

  this.prefs.addListeners(DtPrefs3D.ORBIT_PAST_WORLD_POLES, (value) => {
    this.navigation.setOrbitPastWorldPoles(value);
  });

  this.prefs.addListeners(DtPrefs.LEFT_HANDED_MOUSE_SETUP, (value) => {
    this.navigation.setUseLeftHandedInput(value);
    EventUtils.setUseLeftHandedInput(value);
  });

  this.prefs.addListeners(DtPrefs.WHEEL_SETS_PIVOT, (value) => {
    this.navigation.setWheelSetsPivot(value);
  });

  this.prefs.addListeners(DtPrefs3D.SELECTION_MODE, (mode) => {
    this.impl.selector.setSelectionMode(mode);
  });

  this.prefs.addListeners(DtPrefs.BACKGROUND_COLOR_PRESET, (bacStr) => {
    if (bacStr) {
      try {
        var bac = JSON.parse(bacStr);
        this.impl.setClearColors(bac[0], bac[1], bac[2], bac[3], bac[4], bac[5]);
      } catch (e) {
        logger.warn('Invalid DtPrefs.BACKGROUND_COLOR_PRESET: ' + bac);
      }
    }
  });

  const prevDoubleSided = {};
  // LMV-5379 - add preference to turn double sided materials on and off
  // If the value is a boolean, all of the models will get their materials toggled.
  // The value can also be an object, where the key is the model id and the value is the flag
  // that is used to turn on and off the model's double sided materials.
  this.prefs.addListeners(DtPrefs3D.FORCE_DOUBLE_SIDED, (value) => {
    // Returns true if the model's materials needs to be update.
    function getEnableFlag(model) {
      if (typeof value === 'boolean') {
        return value;
      }
      return !!value[model.id];
    }

    this._setDoubleSided = (event, update = true) => {
      let model = event.model;
      let enable = getEnableFlag(model);
      const modelData = model.getData();
      if (!prevDoubleSided[model.id]) {
        prevDoubleSided[model.id] = !!modelData.doubleSided;
      }

      // Use the model's doubleSided flag if the value is set to false.
      // NOTE: this means that if the model has the doubleSided flag set to true,
      // this preference will not turn off double sided materials.
      const isDoubleSided = enable || !!modelData.doubleSided;
      if (isDoubleSided !== prevDoubleSided[model.id]) {
        this.impl.setDoubleSided(isDoubleSided, model, update);
        prevDoubleSided[model.id] = isDoubleSided;
      }
    };

    var models = this.impl.modelQueue().getModels();
    if (models.length === 0) {
      // Add an event listener
      this.addEventListener(et.MODEL_ADDED_EVENT, this._setDoubleSided);
    } else {
      models.forEach((model) => {
        this._setDoubleSided({ model }, false);
      });
      this.impl.sceneUpdated();
    }
  });

  this.prefs.addListeners(DtPrefs.DISPLAY_OBJECT_INFO, this.onStatusBarPrefChanged, null);
};

/**
 * Locks or unlocks navigation controls.
 *
 * When navigation is locked, certain operations (for example, orbit, pan, or fit-to-view)
 * are disabled.
 *
 * Reference {@link Autodesk.Viewing.Viewer3D#setNavigationLockSettings}
 *
 * @param {boolean} value True if the navigation should be locked.
 * @returns {boolean} The previous state of the lock (this may be used to restore the lock to it's previous state).
 *
 * @alias Autodesk.Viewing.Viewer3D#setNavigationLock
 */
Viewer3D.prototype.setNavigationLock = function (value)
{
  const prev = this.navigation.getIsLocked();
  if (prev !== value) {
    this.navigation.setIsLocked(value);
    this.dispatchEvent({ type: et.NAVIGATION_MODE_CHANGED_EVENT, id: this.getActiveNavigationTool() });
  }

  return prev;
};

/**
 * Gets the current state of the navigation lock.
 * @returns {boolean} True if the navigation controls are currently locked.
 *
 * @alias Autodesk.Viewing.Viewer3D#getNavigationLock
 */
Viewer3D.prototype.getNavigationLock = function ()
{
  return this.navigation.getIsLocked();
};

/**
 * Updates the configuration of the navigation lock,
 * i.e., which actions are available when navigation is locked.
 *
 * The configurable actions are 'orbit', 'pan', 'zoom', 'roll', 'fov', 'walk', or 'gotoview'.
 * By default, none of the actions are enabled when the navigation is locked.
 *
 * Reference {@link Autodesk.Viewing.Viewer3D#setNavigationLock}
 *
 * @param {object} settings Map of <action>:<boolean> pairs specifying
 * whether the given action is *enabled* even when the navigation is locked.
 *
 * @alias Autodesk.Viewing.Viewer3D#setNavigationLockSettings
 */
Viewer3D.prototype.setNavigationLockSettings = function (settings)
{
  this.navigation.setLockSettings(settings);
  this.dispatchEvent({ type: et.NAVIGATION_MODE_CHANGED_EVENT, id: this.getActiveNavigationTool() });
};

/**
 * Gets the current configuration of the navigation lock.
 *  @returns {object} Map of <action>:<boolean> pairs specifying
 * whether the given action is *enabled* even when the navigation is locked.
 *
 * @alias Autodesk.Viewing.Viewer3D#getNavigationLockSettings
 */
Viewer3D.prototype.getNavigationLockSettings = function ()
{
  return this.navigation.getLockSettings();
};

/**
 * Swaps the current navigation tool for the tool with the provided name.
 * Will trigger NAVIGATION_MODE_CHANGED event if the mode actually changes.
 *
 * Reference {@link Viewer3D#getActiveNavigationTool|getActiveNavigationTool()}
 *
 * @param {string} [toolName] - The name of the tool to activate. By default it will switch to the default tool.
 *
 * @returns {boolean} - True if the tool was set successfully. False otherwise.
 *
 * @alias Autodesk.Viewing.Viewer3D#setActiveNavigationTool
 */
Viewer3D.prototype.setActiveNavigationTool = function (toolName)
{

  if (toolName === this._pushedTool || !toolName && !this._pushedTool)
  return true;


  if (this._pushedTool) {

    if (!this.impl.controls.deactivateTool(this._pushedTool)) {
      return false;
    }

    // Need to reset the activeName of the default tool, since "orbit",
    // "freeorbit", "dolly" and "pan" share the same instance.
    this.impl.controls.setToolActiveName(this.getDefaultNavigationToolName());
    this._pushedTool = null;
  }

  var isDefault = !toolName || toolName === this.getDefaultNavigationToolName();

  if (isDefault && this._pushedTool === null) {
    this.dispatchEvent({ type: et.NAVIGATION_MODE_CHANGED_EVENT, id: this.getDefaultNavigationToolName() });
    return true;
  }

  if (this.impl.controls.activateTool(toolName)) {
    this._pushedTool = toolName;
    this.dispatchEvent({ type: et.NAVIGATION_MODE_CHANGED_EVENT, id: this._pushedTool });
    return true;
  }

  return false;
};

/**
 * Returns the name of the active navigation tool.
 *
 * Reference {@link Viewer3D#setActiveNavigationTool|setActiveNavigationTool()}
 *
 * @returns {string} - The tool's name.
 *
 * @alias Autodesk.Viewing.Viewer3D#getActiveNavigationTool
 */
Viewer3D.prototype.getActiveNavigationTool = function ()
{
  return this._pushedTool ? this._pushedTool : this._defaultNavigationTool;
};

/**
 * Sets the default navigation tool. This tool will always sit beneath the navigation tool on the tool stack.
 *
 * @param {string} toolName - The name of the new default navigation tool.
 *
 * @alias Autodesk.Viewing.Viewer3D#setDefaultNavigationTool
 */
Viewer3D.prototype.setDefaultNavigationTool = function (toolName)
{
  if (this._defaultNavigationTool) {
    this.impl.controls.deactivateTool(this._defaultNavigationTool);
  }

  var pushed = this._pushedTool;
  this._pushedTool = null;
  if (pushed) {
    this.impl.controls.deactivateTool(pushed);
  }

  this.impl.controls.activateTool(toolName);
  this._defaultNavigationTool = toolName;

  if (pushed) {
    this.impl.controls.activateTool(pushed);
    this._pushedTool = pushed;
  }
};

/**
 * Returns the default navigation tool
 *
 * @returns {Object} - The default navigation tool.
 *
 * @alias Autodesk.Viewing.Viewer3D#getDefaultNavigationToolName
 */
Viewer3D.prototype.getDefaultNavigationToolName = function ()
{
  return this._defaultNavigationTool;
};

/**
 * Gets the current camera vertical field of view.
 * @returns { number } - the field of view in degrees.
 *
 * @alias Autodesk.Viewing.Viewer3D#getFOV
 */
Viewer3D.prototype.getFOV = function ()
{
  return this.navigation.getVerticalFov();
};

/**
 * Sets the current cameras vertical field of view.
 * @param { number } degrees - Field of view in degrees.
 *
 * @alias Autodesk.Viewing.Viewer3D#setFOV
 */
Viewer3D.prototype.setFOV = function (degrees)
{
  this.navigation.setVerticalFov(degrees, true);
};

/**
 * Gets the current camera focal length.
 * @returns { number } - the focal length in millimetres.
 *
 * @alias Autodesk.Viewing.Viewer3D#getFocalLength
 */
Viewer3D.prototype.getFocalLength = function ()
{
  return this.navigation.getFocalLength();
};

/**
 * Sets the current cameras focal length.
 * @param { number } mm - Focal length in millimetres
 *
 * @alias Autodesk.Viewing.Viewer3D#setFocalLength
 */
Viewer3D.prototype.setFocalLength = function (mm)
{
  this.navigation.setFocalLength(mm, true);
};

/**
 * Hides all lines in the scene.
 * @param {boolean} hide
 *
 * @alias Autodesk.Viewing.Viewer3D#hideLines
 */
Viewer3D.prototype.hideLines = function (hide) {
  if (this.model && this.model.is2d()) {
    logger.warn("Viewer3D.hideLines is not applicable to 2D");
    return;
  }
  this.prefs.set(DtPrefs3D.LINE_RENDERING, !hide);
};

/**
 * Hides all points in the scene.
 * @param {boolean} hide
 *
 * @alias Autodesk.Viewing.Viewer3D#hidePoints
 */
Viewer3D.prototype.hidePoints = function (hide) {
  this.prefs.set(DtPrefs.POINT_RENDERING, !hide);
};

/**
 * Turns edge topology display on/off (where available).
 * @param {boolean} show - true to turn edge topology display on, false to turn edge topology display off.
 *
 * @alias Autodesk.Viewing.Viewer3D#setDisplayEdges
 */
Viewer3D.prototype.setDisplayEdges = function (show) {
  if (this.model && this.model.is2d()) {
    logger.warn("Viewer3D.setDisplayEdges is not applicable to 2D");
    return;
  }
  this.prefs.set(DtPrefs3D.EDGE_RENDERING, show);
};

/**
 * Fits camera to objects by ID. It fits the entire model if no ID is provided.
 * Operation will fit to the model's bounding box when its object tree is not available.
 *
 * @param {number[]|null} [objectIds=null] array of Ids to fit into the view. Avoid passing this value to fit the entire model.
 * @param {Autodesk.Viewing.Model|null} [model] - The model containing the ``objectIds``. If falsey, the viewer's current model will be used.
 * @param {boolean} [immediate=false] - true to avoid the default transition.
 *
 * @fires Autodesk.Viewing#FIT_TO_VIEW_EVENT
 * @fires Autodesk.Viewing#AGGREGATE_FIT_TO_VIEW_EVENT
 *
 * @alias Autodesk.Viewing.Viewer3D#fitToView
 */
Viewer3D.prototype.fitToView = function (objectIds, model, immediate) {

  var selection = [];
  if (objectIds) {
    if (objectIds.length > 0 && objectIds[0].model) {
      // Aggregated selection being passed in.
      selection = objectIds;
    } else if (objectIds.length > 0) {
      // Backwards compatibility interface for single model.
      selection.push({
        model: model || this.model,
        selection: objectIds
      });
    }
  }

  fitToView(this, selection, immediate);
  // Event gets fired from `impl`
};

/**
 * Modifies a click action configuration entry.
 * @param {string} what - which click config to modify (one of "click", "clickAlt", "clickCtrl", "clickShift", "clickCtrlShift").
 * @param {string} where - hit location selector (one of "onObject", "offObject").
 * @param {string[]} newAction - action list (containing any of "setCOI", "selectOnly", "selectToggle", "deselectAll", "deselectAll", "isolate", "showAll", "hide", "focus").
 * @returns {boolean} False if specified entry is not found, otherwise true.
 *
 * @alias Autodesk.Viewing.Viewer3D#setClickConfig
 */
Viewer3D.prototype.setClickConfig = function (what, where, newAction) {
  var config = this.clickHandler ? this.clickHandler.getClickBehavior() :
  this.impl.controls.getClickBehavior();

  if (what in config)
  {
    var actions = config[what];
    if (where in actions)
    {
      actions[where] = newAction;
      return true;
    }
  }
  return false;
};

/**
 * Fetch a click action configuration entry.
 * @param {string} what - which click config to fetch (one of "click", "clickAlt", "clickCtrl", "clickShift", "clickCtrlShift").
 * @param {string} where - hit location selector (one of "onObject", "offObject").
 * @returns {array} action list for the given entry or null if not found.
 *
 * @alias Autodesk.Viewing.Viewer3D#getClickConfig
 */
Viewer3D.prototype.getClickConfig = function (what, where)
{
  var config = this.clickHandler ? this.clickHandler.getClickBehavior() :
  this.impl.controls.getClickBehavior();

  if (what in config)
  {
    var actions = config[what];
    if (where in actions)
    return actions[where];
  }
  return null;
};

/**
 * Modify the default click behaviour for the viewer.
 * @param {boolean} state - If true the default is to set the center of interest. If false the default is single select.
 * @param {boolean} [updatePrefs=true] - If true, the user preferences will be updated.
 *
 * @alias Autodesk.Viewing.Viewer3D#setClickToSetCOI
 */
Viewer3D.prototype.setClickToSetCOI = function (state, updatePrefs)
{
  // Typically, the set{Blah} functions set a preference. This function will set the preference and then call an internal API.
  if (updatePrefs !== false)
  this.prefs.set(DtPrefs3D.CLICK_TO_SET_COI, state);

  var currentOn = this.getClickConfig("click", "onObject");
  if (state)
  {
    if (currentOn.indexOf("setCOI") === -1) // Not already set?
      {
        this.setClickConfig("click", "onObject", ["setCOI"]);
      }
  } else
  if (currentOn.indexOf("setCOI") >= 0) // Is currently set?
    {
      this.setClickConfig("click", "onObject", ["selectOnly"]);
    }
};

/**
 * Updates viewer settings encapsulated witihn a Profile.
 * This method will also load and unload extensions referenced by the Profile.
 *
 * @example
 *   const profileSettings = {
 *      name: "mySettings",
 *      settings: {
 *          ambientShadows: false,
 *          groundShadows: true
 *      }
 *      extensions: {
 *          load: [],   // Extension IDs
 *          unload: []  // Extension IDs
 *      }
 *   }
 *   const profile = new Autodesk.Viewing.Profile(profileSettings);
 *   viewer.setProfile(profile);
 *
 * @param {Autodesk.Viewing.Profile} profile - profile containing settings.
 * @param {boolean} [override] - If set to true this will override all existing preference with the new profile preference. Default: true
 *
 * @alias Autodesk.Viewing.Viewer3D#setProfile
 */
Viewer3D.prototype.setProfile = function (profile, override) {

  if (!profile)
  return false;

  this.profile = profile;
  this.profile.apply(this.prefs, override);

  const toUnload = this.profile.extensions && this.profile.extensions.unload || [];
  const toLoad = this.profile.extensions && this.profile.extensions.load || [];

  toUnload.forEach((extId) => {
    if (this.isExtensionLoaded(extId)) {
      this.unloadExtension(extId);
    }
  });

  toLoad.forEach((extId) => {
    if (!this.isExtensionLoaded(extId)) {
      this.loadExtension(extId, this.config);
    }
  });
  this.dispatchEvent({ type: Autodesk.Viewing.PROFILE_CHANGE_EVENT, profile });
};

Viewer3D.prototype.initPreferencesProfile = function (options = {}) {

  // Setup of AO, Ghosting, Env Lighting etc.
  // This will also setup the viewer.profile only for the web version of the viewer.
  // This should work with the nodejs version, but loading the settings from the loadModel function causes some methods to be undefined.

  // Initialize the profile only if the viewer.profile is not set.
  // This will insure that the loadModel model functions sets the settings once.
  if (!options.skipPrefs) {
    if (!isNodeJS() && !this.profile) {
      let profile = options.profile || TandemProfile;
      this.setProfile(profile, false);
    }
  } else {
    // These preferences are expected to be always available even if no profile is loaded
    if (this.prefs.get(DtPrefs.DISPLAY_UNITS) === undefined) {
      this.prefs.set(DtPrefs.DISPLAY_UNITS, new EnumType(displayUnitsEnum));
    }
    if (this.prefs.get(DtPrefs.DISPLAY_UNITS_PRECISION) === undefined) {
      this.prefs.set(DtPrefs.DISPLAY_UNITS_PRECISION, new EnumType(displayUnitsPrecisionEnum));
    }
  }
};

/**
 * Sets the Light Presets (Environments) for the Viewer.
 *
 * Not applicable to 2D.
 *
 * Sets the preference in the UI
 * @param {Number|String} index - Name (preferred) or index (deprecated) of the light preset. The index mapping looks like this: 0 -> Simple Grey, 1 -> Sharp Highlights, 2 -> Dark Sky, 3 -> Grey Room, 4 -> Photo Booth, 5 -> Tranquility, 6 -> Infinity Pool, 7 -> Simple White, 8 -> Riverbank, 9 -> Contrast, 1 ->0 Rim Highlights, 1 ->1 Cool Light, 1 ->2 Warm Light, 1 ->3 Soft Light, 1 ->4 Grid Light, 1 ->5 Plaza, 1 ->6 Snow Field
 * @note this list is copied from the ones in Environments.js
 *
 * @alias Autodesk.Viewing.Viewer3D#setLightPreset
 */
Viewer3D.prototype.setLightPreset = function (index) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setLightPreset is not applicable to 2D");
    return;
  }
  let name;
  if (typeof index === "number") {
    if (index < 0 || LightPresets.length <= index) {
      index = DefaultLightPreset;
    }
    name = LightPresets[index].name;
  } else {
    name = index;
  }

  this.prefs.set(DtPrefs3D.LIGHT_PRESET, name);
};

/**
 *  Set or unset a view navigation option which requests that orbit controls always orbit around the currently set pivot point.
 *
 *  Sets the preference in the UI
 *  @param {boolean} value - value of the option, true to request use of the pivot point. When false some controls may pivot around the center of the view. (Currently applies only to the view-cube orbit controls.)
 *
 * @alias Autodesk.Viewing.Viewer3D#setUsePivotAlways
 */
Viewer3D.prototype.setUsePivotAlways = function (value) {
  if (this.model && this.model.is2d()) {
    logger.warn("Viewer3D.setUsePivotAlways is not applicable to 2D");
    return;
  }
  this.prefs.set(DtPrefs3D.ALWAYS_USE_PIVOT, value);
};

/**
 * Set or unset a view navigation option to reverse the default direction for camera dolly (zoom) operations.
 *
 *  Sets the preference in the UI
 *  @param {boolean} value - value of the option, true for reverse, false for default
 *
 * @alias Autodesk.Viewing.Viewer3D#setReverseZoomDirection
 */
Viewer3D.prototype.setReverseZoomDirection = function (value) {
  this.prefs.set(DtPrefs.REVERSE_MOUSE_ZOOM_DIR, value);
};

/**
 * Set or unset a view navigation option to reverse the default direction for horizontal look operations.
 *
 * Not applicable to 2D.
 *
 *  Sets the preference in the UI
 *  @param {boolean} value - value of the option, true for reverse, false for default
 *
 * @alias Autodesk.Viewing.Viewer3D#setReverseHorizontalLookDirection
 */
Viewer3D.prototype.setReverseHorizontalLookDirection = function (value) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setReverseHorizontalLookDirection is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.REVERSE_HORIZONTAL_LOOK_DIRECTION, value);
};

/**
 * Set or unset a view navigation option to reverse the default direction for vertical look operations.
 *
 * Not applicable to 2D.
 *
 *  Sets the preference in the UI
 *  @param {boolean} value - value of the option, true for reverse, false for default
 *
 * @alias Autodesk.Viewing.Viewer3D#setReverseVerticalLookDirection
 */
Viewer3D.prototype.setReverseVerticalLookDirection = function (value) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setReverseVerticalLookDirection is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.REVERSE_VERTICAL_LOOK_DIRECTION, value);
};

/**
 * Get the state of the view navigation option that requests the default direction for camera dolly (zoom) operations to be towards the camera pivot point.
 *
 *  Sets the preference in the UI
 *  @param {boolean} value - value of the option, true for towards the pivot, false for default
 *
 * @alias Autodesk.Viewing.Viewer3D#setZoomTowardsPivot
 */
Viewer3D.prototype.setZoomTowardsPivot = function (value) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setZoomTowardsPivot is not applicable to 2D");
    return;
  }
  this.prefs.set(DtPrefs3D.ZOOM_TOWARDS_PIVOT, value);
};

/**
 * Set or unset a view navigation option to allow the orbit controls to move the camera beyond the north and south poles (world up/down direction). In other words, when set the orbit control will allow the camera to rotate into an upside down orientation. When unset orbit navigation should stop when the camera view direction reaches the up/down direction.
 *
 * Not applicable to 2D.
 *
 *  Sets the preference in the UI
 *  @param {boolean} value - value of the option, true to allow orbiting past the poles.
 *
 * @alias Autodesk.Viewing.Viewer3D#setOrbitPastWorldPoles
 */
Viewer3D.prototype.setOrbitPastWorldPoles = function (value) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setOrbitPastWorldPoles is not applicable to 2D");
    return;
  }

  this.prefs.set(DtPrefs3D.ORBIT_PAST_WORLD_POLES, value);
};

/**
 * Set or unset a view navigation option which requests that mouse buttons be reversed from their default assignment (i.e. Left mouse operation becomes right mouse and vice versa).
 *
 *  Sets the preference in the UI
 *  @param {boolean} value - value of the option, true to request reversal of mouse button assignments.
 *
 * @alias Autodesk.Viewing.Viewer3D#setUseLeftHandedInput
 */
Viewer3D.prototype.setUseLeftHandedInput = function (value) {
  this.prefs.set(DtPrefs.LEFT_HANDED_MOUSE_SETUP, value);
};

/**
 * Set units for quantities displayed in the property panel.
 * Only setting linear (distance) quantity units are supported
 *
 *  @param {string} value - display units to set.
 *
 * The units can be "" (file units), "mm", "cm", "m", "in", "ft", "ft-and-fractional-in",
 * "ft-and-decimal-in", "decimal-in", "decimal-ft", "fractional-in", "m-and-cm",
 *
 * @alias Autodesk.Viewing.Viewer3D#setDisplayUnits
 */
Viewer3D.prototype.setDisplayUnits = function (value) {
  this.prefs.set(DtPrefs.DISPLAY_UNITS, value);
};

/**
 * Set the precision for quantities displayed in the property panel.
 *
 *  @param {number} value - precision for the units
 * The value is the number of decimal values after the ".".
 * For e.g., a value of
 * 5 would be either 0.12345 or 1/32 (2^5) for fraction type units
 * Values greater than 6 is not supported
 *
 * If value is not specified, it defaults to the precision
 * in the file
 * @alias Autodesk.Viewing.Viewer3D#setDisplayUnitsPrecision
 */
Viewer3D.prototype.setDisplayUnitsPrecision = function (value) {
  if (value === null || value === undefined) {
    value = "";
  }
  this.prefs.set(DtPrefs.DISPLAY_UNITS_PRECISION, value);
};

/**
 * If enabled, set ground shadow color
 *
 * Not applicable to 2D
 *
 * @param {THREE.Color} color
 *
 * @alias Autodesk.Viewing.Viewer3D#setGroundShadowColor
 */
Viewer3D.prototype.setGroundShadowColor = function (color) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setGroundShadowColor is not applicable to 2D");
    return;
  }

  this.impl.setGroundShadowColor(color);
};

/**
 * If enabled, set ground shadow alpha
 *
 * Not applicable to 2D
 *
 * @param {float} alpha
 *
 * @alias Autodesk.Viewing.Viewer3D#setGroundShadowAlpha
 */
Viewer3D.prototype.setGroundShadowAlpha = function (alpha) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setGroundShadowAlpha is not applicable to 2D");
    return;
  }

  this.impl.setGroundShadowAlpha(alpha);
};

/**
 * If enabled, set ground reflection color. This is reset to default when reflections toggled off.
 *
 * Not applicable to 2D
 *
 * @param {THREE.Color} color
 *
 * @alias Autodesk.Viewing.Viewer3D#setGroundReflectionColor
 */
Viewer3D.prototype.setGroundReflectionColor = function (color) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setGroundReflectionColor is not applicable to 2D");
    return;
  }

  this.impl.setGroundReflectionColor(color);
};

/**
 * If enabled, set ground reflection alpha. This is reset to default when reflections toggled off.
 *
 * Not applicable to 2D
 *
 * @param {number} alpha
 *
 * @alias Autodesk.Viewing.Viewer3D#setGroundReflectionAlpha
 */
Viewer3D.prototype.setGroundReflectionAlpha = function (alpha) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.setGroundReflectionAlpha is not applicable to 2D");
    return;
  }

  this.impl.setGroundReflectionAlpha(alpha);
};

/**
 * Returns a list of active cut planes
 *
 * Not applicable to 2D
 *
 * @return {THREE.Vector4[]} List of Vector4 plane representation {x:a, y:b, z:c, w:d}
 *
 * @alias Autodesk.Viewing.Viewer3D#getCutPlanes
 */
Viewer3D.prototype.getCutPlanes = function () {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.getCutPlanes is not applicable to 2D");
    return [];
  }

  return this.impl.getCutPlanes();
};

/**
 * Apply a list of cut planes
 *
 * Not applicable to 2D
 *
 * @param {THREE.Vector4[]} planes - List of Vector4 plane representation: {x:a, y:b, z:c, w:d}
 * Plane general equation: ax + by + cz + d = 0 where a, b, and c are not all zero
 * Passing an empty list or null is equivalent to setting zero cut planes
 *
 * @alias Autodesk.Viewing.Viewer3D#setCutPlanes
 */
Viewer3D.prototype.setCutPlanes = function (planes) {
  if (this.model && this.model.is2d())
  {
    logger.warn("Viewer3D.getCutPlanes is not applicable to 2D");
    return;
  }
  this.impl.setCutPlaneSet("__set_view", planes);
};

/**
 * Captures the current screen image as Blob URL
 * Blob URL can be used like a regular image url (e.g., window.open, img.src, etc)
 * If width and height are 0, returns asynchronously and calls the callback with an image as Blob URL with dimensions equal to current canvas dimensions
 * If width and height are given, returns asynchronously and calls the callback with the resized image as Blob URL
 * If no callback is given, displays the image in a new window.
 * Optional overlayRenderer can be supplied, in order to render an overlay on top of the renderer image.
 * @param  {number} [w]  width of the requested image
 * @param  {number} [h]  height of the requested image
 * @param  {Function} [cb] callback
 * @param  {Function} [overlayRenderer] overlayRenderer
 * @return {DOMString} screenshot image Blob URL, if no parameters are given
 *
 * @alias Autodesk.Viewing.Viewer3D#getScreenShot
 */
Viewer3D.prototype.getScreenShot = function (w, h, cb, overlayRenderer) {
  return ScreenShot.getScreenShotLegacy(this, w, h, cb, overlayRenderer);
};

/**
 * Sets the object context menu.
 * @param {?ObjectContextMenu=} [contextMenu]
 */
Viewer3D.prototype.setContextMenu = function (contextMenu) {

  if (this.contextMenu) {

    // Hide the current context menu, just in case it's open right now.
    // This does nothing if the context menu is not open.
    //
    this.contextMenu.hide();
  }

  this.contextMenu = contextMenu || null; // to avoid undefined
};

/**
 * Activates the default context menu.<br>
 * Contains options Isolate, Hide selected, Show all objects, Focus and Clear selection.
 *
 * @returns {boolean} Whether the default context menu was successfully set (true) or not (false)
 */
Viewer3D.prototype.setDefaultContextMenu = function () {

  var ave = Autodesk.Viewing.Extensions;
  if (ave && ave.ViewerObjectContextMenu) {
    this.setContextMenu(new ave.ViewerObjectContextMenu(this));
    return true;
  }
  return false;
};

Viewer3D.prototype.triggerContextMenu = function (event) {
  if (this.config && this.config.onTriggerContextMenuCallback) {
    this.config.onTriggerContextMenuCallback(event);
  }

  if (this.contextMenu) {
    this.contextMenu.show(event);
    return true;
  }
  return false;
};

Viewer3D.prototype.triggerSelectionChanged = function (dbId) {
  if (this.config && this.config.onTriggerSelectionChangedCallback) {
    this.config.onTriggerSelectionChangedCallback(dbId);
  }
};

Viewer3D.prototype.triggerDoubleTapCallback = function (event) {
  if (this.config && this.config.onTriggerDoubleTapCallback) {
    this.config.onTriggerDoubleTapCallback(event);
  }
};

Viewer3D.prototype.triggerSingleTapCallback = function (event) {
  if (this.config && this.config.onTriggerSingleTapCallback) {
    this.config.onTriggerSingleTapCallback(event);
  }
};

Viewer3D.prototype.triggerSwipeCallback = function (event) {
  if (this.config && this.config.onTriggerSwipeCallback) {
    this.config.onTriggerSwipeCallback(event);
  }
};

Viewer3D.prototype.initContextMenu = function () {
  if (!this.contextMenu) {
    this.setDefaultContextMenu();
  }

  // Disable the browser's default context menu by default, or if explicitly specified.
  //
  var disableBrowserContextMenu = !this.config || this.config.disableBrowserContextMenu !== false;
  if (disableBrowserContextMenu) {
    this.onDefaultContextMenu = function (e) {
      e.preventDefault();
    };
    this.container.addEventListener('contextmenu', this.onDefaultContextMenu, false);
  }

  var self = this;

  var canvas = this.canvas || this.container;

  this.onMouseDown = function (event) {
    if (EventUtils.isRightClick(event)) {
      self.startX = event.clientX;
      self.startY = event.clientY;
    }
  };

  canvas.addEventListener('mousedown', this.onMouseDown);

  this.onMouseUp = function (event) {
    if (EventUtils.isRightClick(event) && event.clientX === self.startX && event.clientY === self.startY) {
      self.triggerContextMenu(event);
    }
    return true;
  };

  canvas.addEventListener('mouseup', this.onMouseUp, false);
};


/**
 * Registers a new callback that modifies the context menu.
 * This allows extensions and others to add, remove, or change items in the context menu.
 * Extensions that call registerContextMenuCallback() should call unregisterContextMenuCallback() in their unload().
 *
 * @see Viewer.unregisterContextMenuCallback
 * @see ObjectContextMenu.buildMenu
 *
 * @param {string} id - Unique id to identify this callback. Used by unregisterContextMenuCallback().
 * @param {function(Array, Object)} callback - Will be called before the context menu is displayed.
 *
 * @example
 * // Here's an example that appends a new context menu item:
 *
 * viewer.registerContextMenuCallback('MyExtensionName', function (menu, status) {
 *     if (status.hasSelected) {
 *         menu.push({
 *             title: 'My new context menu item with selected objects',
 *             target: function () {
 *                 alert('Do something with selected objects');
 *         }});
 *     } else {
 *         menu.push({
 *             title: 'My new context menu item, no selected objects',
 *             target: function () {
 *                 alert('Do something else');
 *         }});
 *     }
 * });
 */
Viewer3D.prototype.registerContextMenuCallback = function (id, callback) {
  this.contextMenuCallbacks[id] = callback;
};

/**
 * Unregisters an existing callback that modifies the context menu.
 * Extensions that call registerContextMenuCallback() should call unregisterContextMenuCallback() in their unload().
 * @param {string} id - Unique id to identify this callback.
 * @returns {boolean} true if the callback was unregistered successfully.
 * @see Viewer.registerContextMenuCallback
 */
Viewer3D.prototype.unregisterContextMenuCallback = function (id) {
  if (id in this.contextMenuCallbacks) {
    delete this.contextMenuCallbacks[id];
    return true;
  }
  return false;
};

/**
 * Runs all registered context menu callbacks.
 * @param {array} menu - Context menu items.
 * @param {Object} status - Information about nodes.
 * @param {Event} status.event - The UI event that triggered the context menu.
 * @see ObjectContextMenu.buildMenu
 * @private
 */
Viewer3D.prototype.runContextMenuCallbacks = function (menu, status) {
  for (var id in this.contextMenuCallbacks) {
    this.contextMenuCallbacks[id](menu, status);
  }
};

/**
 * Set model units
 * @param Model units
 */
Viewer3D.prototype.setModelUnits = function (modelUnits) {
  if (this.model) {
    this.model.getData().overriddenUnits = modelUnits;
  }
};

/**
 * Calculates the pixel position in client space coordinates of a point in world space.<br>
 * See also
 * {@link Autodesk.Viewing.Viewer3D#clientToWorld|clientToWorld()}.
 * @param {THREE.Vector3} point Point in world space coordinates.
 * @param {THREE.Camera} camera Optional camera to use - default is the viewer's native camera.
 * @returns {THREE.Vector3} Point transformed and projected into client space coordinates. Z value is 0.
 *
 * @alias Autodesk.Viewing.Viewer3D#worldToClient
 */
Viewer3D.prototype.worldToClient = function (point, camera = this.impl.camera) {
  return this.impl.worldToClient(point, camera);
};

/**
 * Given coordinates in pixel screen space it returns information of the underlying geometry node.
 * Hidden nodes will not be taken into account. Returns null if there is no geometry in the specified location.
 * For 2d models, it will return null outside the paper, unless ignore2dModelBounds is true.<br>
 * See also
 * {@link Autodesk.Viewing.Viewer3D#worldToClient|worldToClient()}.
 *
 * @param {Number} clientX - X coordinate where 0 is left
 * @param {Number} clientY - Y coordinate where 0 is top
 * @param {Boolean} [ignoreTransparent] - Ignores transparent materials
 * @param {boolean} [ignore2dModelBounds] - For 2d models - whether to return a result outside of the model's bounds.
 * @returns {Object|null} contains point attribute. 3d models have additional attributes.
 *
 * @alias Autodesk.Viewing.Viewer3D#clientToWorld
 */
Viewer3D.prototype.clientToWorld = function (clientX, clientY, ignoreTransparent, ignore2dModelBounds) {

  return this.impl.clientToWorld(clientX, clientY, ignoreTransparent, ignore2dModelBounds);
};

/**
 * Changes the color of the selection for a particular selection type.
 * - Autodesk.Viewing.SelectionType.MIXED
 *   - Sets the same color for regular and overlayed selection.
 * - Autodesk.Viewing.SelectionType.REGULAR
 *   - Sets the color of regular selection.
 * - Autodesk.Viewing.SelectionType.OVERLAYED
 *   - Sets the color of overlayed selection.
 *
 * @example
 *  viewer.setSelectionColor(new THREE.Color(0xFF0000), Autodesk.Viewing.SelectionType.MIXED); // red color
 * @param {THREE.Color} color
 * @param {number} selectionType a member of Autodesk.Viewing.SelectionMode.
 * @alias Autodesk.Viewing.Viewer3D#setSelectionColor
 */
Viewer3D.prototype.setSelectionColor = function (color, selectionType) {
  this.impl.setSelectionColor(color, selectionType);
};

/**
 * Changes the color of the selection for 2D drawings.
 *
 * @example
 *  viewer.set2dSelectionColor(new THREE.Color(0xFF0000), 0.1); // red color, opacity of 0.1
 * @param {THREE.Color} color
 * @param {number} opacity
 *
 * @alias Autodesk.Viewing.Viewer3D#set2dSelectionColor
 */
Viewer3D.prototype.set2dSelectionColor = function (color, opacity) {
  this.impl.set2dSelectionColor(color, opacity);
};

/**
 * Sets the current UI theme of the viewer.
 * Supported values are "light-theme" and "dark-theme", which is the default.
 *
 * @param {string} name - Name of the theme, it will be added to the viewer's container class list.
 *
 * @alias Autodesk.Viewing.Viewer3D#setTheme
 */
Viewer3D.prototype.setTheme = function (name) {

  var classList = this.container.classList;

  // Remove previous themes.
  for (var i = 0; i < classList.length; ++i) {
    var index = classList[i].indexOf('-theme');
    if (index !== -1) {
      classList.remove(classList[i--]);
    }
  }

  // Set current theme.
  classList.add(name);
};

/**
 * Highlight an object with a theming color that is blended with the original object's material.
 * @param {number} dbId
 * @param {THREE.Vector4} color - (r, g, b, intensity), all in [0,1].
 * @param {Autodesk.Viewing.Model} [model] - For multi-model support.
 * @param {boolean} [recursive] - Should apply theming color recursively to all child nodes.
 *
 * @alias Autodesk.Viewing.Viewer3D#setThemingColor
 */
Viewer3D.prototype.setThemingColor = function (dbId, color, model, recursive) {
  // use default RenderModel by default
  model = model || this.model;

  model.setThemingColor(dbId, color, recursive);

  // we changed the scene to apply theming => trigger re-render
  this.impl.invalidate(true);
};

/**
 * Restore original colors for all themed shapes.
 * @param {Autodesk.Viewing.Model} [model] - For multi-model support.
 *
 * @alias Autodesk.Viewing.Viewer3D#clearThemingColors
 */
Viewer3D.prototype.clearThemingColors = function (model) {
  // use default RenderModel by default
  model = model || this.model;

  model.clearThemingColors();

  // we changed the scene to apply theming => trigger re-render
  this.impl.invalidate(true);
};

/**
 * Temporarily remove a model from the Viewer, but keep loaders, materials, and geometry alive.
 *
 * Reference {@link Autodesk.Viewing.Viewer3D#showModel}
 *
 * @param {number|Autodesk.Viewing.Model} model - model id or Model object
 * @returns {boolean} true indicates success, i.e., modelId referred to a visible model that is now hidden
 *
 * @alias Autodesk.Viewing.Viewer3D#hideModel
 */
Viewer3D.prototype.hideModel = function (model) {
  var scene = this.impl.modelQueue();
  // find visible model with this id
  model = typeof model === 'number' ? scene.findModel(model) : model;

  if (!model) {
    return false;
  }

  // remove model from viewer - but without discarding materials
  this.impl.removeModel(model);

  // make this model available for later showModel() calls
  scene.addHiddenModel(model);

  return true;
};

/**
 * Make a previously hidden model visible again.
 *
 * Reference {@link Autodesk.Viewing.Viewer3D#hideModel}
 *
 * @param {number|Autodesk.Viewing.Model} model - model id or Model object
 * @param {boolean} preserveTools - disable automatic activation of default tool
 * @returns {boolean} true indicates success, i.e., ``model`` referred to a hidden model that is now visible
 *
 * @alias Autodesk.Viewing.Viewer3D#showModel
 */
Viewer3D.prototype.showModel = function (model, preserveTools) {
  var scene = this.impl.modelQueue();
  model = typeof model === 'number' ? scene.findHiddenModel(model) : model;

  if (!model) {
    // modelId does not refer to any hidden model
    return false;
  }

  // remove model from list of hidden ones
  scene.removeHiddenModel(model);

  // add it to the viewer.
  this.impl.addModel(model, preserveTools);

  return true;
};

/**
 * @returns {Autodesk.Viewing.Model[]}
 *
 * @alias Autodesk.Viewing.Viewer3D#getVisibleModels
 */
Viewer3D.prototype.getVisibleModels = function () {
  var scene = this.impl.modelQueue();
  return scene.getModels().slice();
};

/**
 * @returns {Autodesk.Viewing.Model[]}
 *
 * @alias Autodesk.Viewing.Viewer3D#getHiddenModels
 */
Viewer3D.prototype.getHiddenModels = function () {
  var scene = this.impl.modelQueue();
  return scene.getHiddenModels().slice();
};

/**
 * Returns all models loaded in the viewer.
 * @returns {Autodesk.Viewing.Model[]} - An array of visible and hidden models
 *
 * @alias Autodesk.Viewing.Viewer3D#getAllModels
 */
Viewer3D.prototype.getAllModels = function () {
  var scene = this.impl.modelQueue();
  return scene.getAllModels().slice();
};


/**
 * @private
 */
Viewer3D.prototype.restoreDefaultSettings = function () {

  var model = this.model;
  var tag = model.is2d() ? '2d' : '3d';
  this.prefs.reset(tag);

  this.impl.setupLighting(model);
  this.impl.setRenderingPrefsFor2D(!model.is3d());

  this.fireEvent({ type: et.RESTORE_DEFAULT_SETTINGS_EVENT });
};

/**
 * Disables roll-over highlighting.
 * @param {boolean} disable - Indicates whether highlighting should be on or off. True to disable highlights, false to enable them.
 *
 * @alias Autodesk.Viewing.Viewer3D#disableHighlight
 */
Viewer3D.prototype.disableHighlight = function (disable) {
  this.impl.disableHighlight(disable);
};

/**
 * disable the selection of a loaded model.
 * @param {boolean} disable - true to disable selection, false to enable selection.
 *
 * @alias Autodesk.Viewing.Viewer3D#disableSelection
 */
Viewer3D.prototype.disableSelection = function (disable) {
  if (disable) {
    this.clearSelection();
  }
  this.impl.disableSelection(disable);
};

/**
 * check if the mouse-over highlight is disabled or not
 *
 * @alias Autodesk.Viewing.Viewer3D#isHighlightDisabled
 */
Viewer3D.prototype.isHighlightDisabled = function () {
  return this.impl.selector.highlightDisabled;
};

/**
 * check if the mouse-over highlight is paused or not
 *
 * @alias Autodesk.Viewing.Viewer3D#isHighlightPaused
 */
Viewer3D.prototype.isHighlightPaused = function () {
  return this.impl.selector.highlightPaused;
};

/**
 * check if the mouse-over highlight is active or not
 * @alias Autodesk.Viewing.Viewer3D#isHighlightActive
 */
Viewer3D.prototype.isHighlightActive = function () {
  return !(this.impl.selector.highlightDisabled || this.impl.selector.highlightPaused);
};

/**
 * check if the selection of the loaded model is disabled or not
 * @alias Autodesk.Viewing.Viewer3D#isSelectionDisabled
 */
Viewer3D.prototype.isSelectionDisabled = function () {
  return this.impl.selector.selectionDisabled;
};

/**
 * Activates the extension based on the extensionID and mode given. By default it takes the first available mode in getmodes();
 * @param {string} extensionID - The extension id.
 * @param {string} [mode]
 *
 * @alias Autodesk.Viewing.Viewer3D#activateExtension
 */
Viewer3D.prototype.activateExtension = function (extensionID, mode) {
  if (this.loadedExtensions && extensionID in this.loadedExtensions) {
    var extension = this.loadedExtensions[extensionID];
    return extension.activate(mode);
  } else {
    logger.warn("Extension is not loaded or doesn't exist");
    return false;
  }
};

/**
 * Dectivates the extension based on the extensionID specified.
 * @param {string} extensionID - the extension ID
 * @returns {boolean} - true if the extension was deactivated false otherwise
 * @alias Autodesk.Viewing.Viewer3D#deactivateExtension
 */
Viewer3D.prototype.deactivateExtension = function (extensionID) {
  if (this.loadedExtensions && extensionID in this.loadedExtensions) {
    var extension = this.loadedExtensions[extensionID];
    return extension.deactivate();
  } else {
    logger.warn("Extension is not loaded or doesn't exist");
    return false;
  }
};

/**
 * Check if the extension is active or not by passing the extensionID.
 * @param {string} extensionID - the extension ID
 * @param {string} mode - The model of the extension
 * @returns {boolean} - True if the extension is active, false otherwise
 * @alias Autodesk.Viewing.Viewer3D#isExtensionActive
 */
Viewer3D.prototype.isExtensionActive = function (extensionID, mode) {
  if (this.loadedExtensions && extensionID in this.loadedExtensions) {
    var extension = this.loadedExtensions[extensionID];
    var activeStatus = extension.isActive(mode);
    return activeStatus;

  } else {
    logger.warn("Extension is not loaded or doesn't exist");
    return false;
  }
};

/**
 * Check if the extension is loaded or not by passing the extensionID.
 * @param {string} extensionID - the extension ID
 * @returns {boolean} - returns true if the extension was loaded, false otherwise
 * @alias Autodesk.Viewing.Viewer3D#isExtensionLoaded
 */
Viewer3D.prototype.isExtensionLoaded = function (extensionID) {
  return this.loadedExtensions && extensionID in this.loadedExtensions;
};

/**
 * Get a list of all the extensions that are currently loaded.
 * @returns {string[]} - returns the IDs of all of the loaded extensions
 * @alias Autodesk.Viewing.Viewer3D#getLoadedExtensions
 */
Viewer3D.prototype.getLoadedExtensions = function () {
  return this.loadedExtensions || [];
};

/**
 * Get a list of all the modes that are available for the given extensionID.
 * @param {string} extensionID - the extension ID
 * @returns {string[]} - array of the extension's modes.
 * @alias Autodesk.Viewing.Viewer3D#getExtensionModes
 */
Viewer3D.prototype.getExtensionModes = function (extensionID) {
  if (this.loadedExtensions && extensionID in this.loadedExtensions) {
    var extension = this.loadedExtensions[extensionID];
    return extension.getModes();
  } else {
    console.warn("Extension is not loaded or doesn't exist");
    return [];
  }
};

/**
 * Object that is returned by the ray cast and hit test methods for each scene object under the given canvas coordinates.
 * @typedef {object} Intersection
 * @property {number} dbId - Internal ID of the scene object.
 * @property {number} distance - Distance of the intersection point from camera. All intersections
 *  returned by the ray casting method are sorted from the smallest distance to the largest.
 * @property {THREE.Face3} face - THREE.Face3 object
 *  representing the triangular mesh face that has been intersected.
 * @property {number} faceIndex - Index of the intersected face, if available.
 * @property {number} fragId - ID of Forge Viewer *fragment* that was intersected.
 * @property {THREE.Vector3} point - THREE.Vector3 point of intersection.
 * @property {Autodesk.Viewing.Model} model - Model instance the dbId belongs to.
 */

/**
 * Returns the intersection information for point x,y. If no intersection is found this function will return null.
 * @param {number} x - X-coordinate, i.e., horizontal distance (in pixels) from the left border of the canvas.
 * @param {number} y - Y-coordinate, i.e., vertical distance (in pixels) from the top border of the canvas.
 * @param {boolean} [ignoreTransparent] - ignores any transparent objects that might intersect x,y
 * @returns {Intersection|null} - Intersection information about closest hit point.
 *
 * @alias Autodesk.Viewing.Viewer3D#hitTest
 */
Viewer3D.prototype.hitTest = function (x, y, ignoreTransparent) {
  return this.impl.hitTest(x, y, ignoreTransparent);
};

/**
 * Clears the screen and redraws the overlays if *clear* is set to true.
 * Only the overlays will be redrawn if *clear* is set to false.
 * Should only be called when absolutely needed.
 *
 * @param {boolean} clear - clears the screen and redraws the overlays.
 * @alias Autodesk.Viewing.Viewer3D#refresh
 */
Viewer3D.prototype.refresh = function (clear) {
  this.impl.invalidate(clear, false, !clear);
};

Viewer3D.prototype.createSettingsPanel = function () {
  var settingsPanel = new ViewerSettingsPanel(this, true);
  this.setSettingsPanel(settingsPanel);
  settingsPanel.syncUI();

  settingsPanel.addEventListeners();

  this.dispatchEvent({ type: SETTINGS_PANEL_CREATED_EVENT });
};

Viewer3D.prototype.getSettingsPanel = function () {
  if (!this.viewerSettingsPanel) {
    this.createSettingsPanel();
  }
  return this.viewerSettingsPanel;
};

Viewer3D.prototype.setSettingsPanel = function (settingsPanel) {
  if (settingsPanel instanceof SettingsPanel || !settingsPanel) {
    if (this.viewerSettingsPanel) {
      this.viewerSettingsPanel.setVisible(false);
      this.viewerSettingsPanel.uninitialize();
      this.viewerSettingsPanel = null;
    }

    this.viewerSettingsPanel = settingsPanel;
  }
};

Viewer3D.prototype.showViewer3dOptions = function (show) {
  var settingsPanel = this.getSettingsPanel(true);
  if (show && settingsPanel.isVisible()) {
    settingsPanel.setVisible(false);
  }
  settingsPanel.setVisible(show);
};

/**
 * An object that holds position details of the visible portion of the viewer that mimics DOMRect structure
 * @typedef {Object} VisibleViewportArea
 * @property {number} x - The x-coordinate of the rectangle's origin.
 * @property {number} y - The y-coordinate of the rectangle's origin.
 * @property {number} width - The width of the rectangle.
 * @property {number} height - The height of the rectangle.
 * @property {number} top - The y-coordinate of the top edge of the rectangle.
 * @property {number} right - The x-coordinate of the right edge of the rectangle.
 * @property {number} bottom - The y-coordinate of the bottom edge of the rectangle.
 * @property {number} left - The x-coordinate of the left edge of the rectangle.
 */

/**
 * Sets the amount of viewable space the viewer has
 * If details is not provided then resets to the default viewable space.
 * @param {VisibleViewportArea} details
 */
Viewer3D.prototype.setVisibleViewportArea = function (details) {
  if (!details) {
    const rect = this.container.getBoundingClientRect();
    this.visibleViewportArea = {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left
    };
    return;
  }
  this.visibleViewportArea = details;
};

/**
 * Returns the details of the viewable area the viewer has, in the form of DOMRect object
 * @returns {VisibleViewportArea}
 */
Viewer3D.prototype.getVisibleViewportArea = function () {
  return this.visibleViewportArea;
};

Viewer3D.prototype.onProgressBarUpdate = function (event) {
  if (event.percent >= 0) {
    this.progressbar.setPercent(event.percent, event.state);
  }
};

Viewer3D.prototype.getStatusBar = function () {
  return this.statusbar;
};